import React, { useState, useEffect, useRef } from 'react';
import { Nav, Tab, Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge, Modal, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMagicWandSparkles, 
  faEye, 
  faCode, 
  faChartBar, 
  faFolderTree,
  faPlus,
  faRefresh,
  faRocket,
  faCopy,
  faSave,
  faDownload,
  faTrash,
  faEdit,
  faCogs,
  faClone,
  faCheckCircle,
  faTimesCircle,
  faPause,
  faPlay,
  faUpload,
  faCloud,
  faDatabase,
  faLink,
  faExclamationTriangle,
  faExternalLinkAlt,
  faCodeBranch,
  faUsers,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { getHatateToken } from '../helpers/utils';
import TinymceEditor from '../components/base/TinymceEditor';
import { UserContext, useUserContext } from './UserContext';


// Helper function para obtener headers de autenticación
/*const getAuthHeaders = () => {
  // Simulamos la función getHatateToken() - en tu caso real usarías la función real
  const token = localStorage.getItem('anubis_token') || 'demo-token-12345';
  return {
    'Content-Type': 'application/json',
    'Authorization': token
  };
}; */

const getAuthHeaders = () => {
  const token = getHatateToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': token  // ✅ SIN "Bearer " - igual que tu ejemplo funcional
  };
};

// Interfaces mejoradas
interface Project {
  id: string;
  name: string;
  slug: string;
  type: 'react' | 'vue' | 'angular';
  status: 'draft' | 'generated' | 'deployed' | 'testing';
  createdAt: string;
  lastModified: string;
  description: string;
  projectPath?: string;
  category: 'dashboard' | 'form' | 'management' | 'communication' | 'utility' | 'custom';
  complexity: 'simple' | 'medium' | 'complex';
  dataSource?: {
    type: 'upload' | 'api' | 'manual';
    config?: any;
  };
  features: string[];
  preview?: string;
  deployUrl?: string;
}

interface AppGenerationRequest {
  projectName: string;
  description: string;
  userLevel: 'beginner' | 'intermediate' | 'advanced';
  category: string;
  features: string[];
  dataRequirements: {
    type: 'upload' | 'api' | 'manual';
    structure?: string;
    endpoint?: string;
  };
  complexity: 'simple' | 'medium' | 'complex';
  designPreferences: {
    theme: 'light' | 'dark' | 'auto';
    primaryColor: string;
    layout: 'modern' | 'classic' | 'minimal';
  };
}

interface ComponentGeneratorResponse {
  success: boolean;
  projectId: string;
  componentCode: string;
  files: Array<{
    filename: string;
    content: string;
    type: 'component' | 'style' | 'config' | 'test';
  }>;
  projectPath?: string;
  previewUrl?: string;
}

const generateSafeSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Eliminar caracteres especiales
    .replace(/\s+/g, '-') // Espacios a guiones
    .replace(/-+/g, '-') // Múltiples guiones a uno solo
    .replace(/^-|-$/g, '') // Eliminar guiones al inicio/final
    .substring(0, 30); // Máximo 30 caracteres
};

const EnhancedComponentStudio: React.FC = () => {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  // Estados para el generador inteligente
  const [generationStep, setGenerationStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  useEffect(() => {
    loadProjects();
  }, []);

  // FUNCIÓN RESTAURADA: Cargar proyectos desde la API
const loadProjects = async () => {
  try {
    setIsLoading(true);
    
    const response = await fetch('https://backend.anubisai.net/api/component-generator/projects', {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (response.ok) {
      const result = await response.json();
      const projects = result.projects || [];
      
      console.log('📦 Projects loaded from API:', projects.length);
      setProjects(projects);
      
      // Si no hay proyectos, dejarlo vacío (NO usar mock data)
      if (projects.length === 0) {
        console.log('📝 No projects found - clean state');
      }
    } else {
      console.error('API Error:', response.status, response.statusText);
      // ❌ CRÍTICO: NO usar mock data como fallback
      setProjects([]);
    }
  } catch (error) {
    console.error('Network Error loading projects:', error);
    // ❌ CRÍTICO: NO usar mock data como fallback
    setProjects([]);
  } finally {
    setIsLoading(false);
  }
};

  const handleDeleteProject = async (project: Project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      // Llamar a API de eliminación
      const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${projectToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
        
        if (selectedProject?.id === projectToDelete.id) {
          setSelectedProject(null);
        }
      } else {
        console.error('Delete API Error:', response.status);
        // Eliminar localmente como fallback
        setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      }

      setShowDeleteModal(false);
      setProjectToDelete(null);
    } catch (error) {
  console.error('Error deleting project:', error);
  setDeleteError(`Error eliminando proyecto: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  // ❌ NO eliminar localmente si la API falla
  // setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
  setShowDeleteModal(false);
  setProjectToDelete(null);
}
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setActiveTab('code-editor');
  };

  // PARTE 3: CODE EDITOR Y PREVIEW - FUNCIONALES CON API

const CodeEditorTab: React.FC<{ project?: Project | null }> = ({ project }) => {
  const { user } = useUserContext();
  const [activeFile, setActiveFile] = useState('');
  const [code, setCode] = useState('');
  const editorStyles = `
<style>
.code-content {
  flex: 1;
  position: relative;
  overflow-y: auto;  /* ✅ AGREGAR scroll vertical */
  overflow-x: auto;  /* ✅ AGREGAR scroll horizontal */
}

/* ✅ AGREGAR estilos para scrollbar personalizado */
.code-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.code-content::-webkit-scrollbar-track {
  background: #2d2d30;
}

.code-content::-webkit-scrollbar-thumb {
  background: #424245;
  border-radius: 4px;
}

.code-content::-webkit-scrollbar-thumb:hover {
  background: #4c4c50;
}

.code-line.ai-typing {
  background: rgba(0, 123, 255, 0.15);
  border-left: 3px solid #007bff;
  animation: typing 1s infinite;
}

.code-line.ai-modified {
  background: rgba(138, 43, 226, 0.15);
  border-left: 3px solid #8a2be2;
  animation: modified 0.5s ease-out;
}

.code-line.ai-added {
  background: rgba(34, 197, 94, 0.15);
  border-left: 3px solid #22c55e;
  animation: added 0.5s ease-out;
}

.code-line.ai-deleted {
  background: rgba(239, 68, 68, 0.15);
  border-left: 3px solid #ef4444;
  text-decoration: line-through;
  animation: deleted 0.5s ease-out;
}

@keyframes typing {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

@keyframes modified {
  0% { 
    background: rgba(138, 43, 226, 0.3);
    transform: translateX(3px);
  }
  100% { 
    background: rgba(138, 43, 226, 0.15);
    transform: translateX(0);
  }
}

@keyframes added {
  0% { 
    background: rgba(34, 197, 94, 0.3);
    transform: translateX(3px);
  }
  100% { 
    background: rgba(34, 197, 94, 0.15);
    transform: translateX(0);
  }
}

@keyframes deleted {
  0% { opacity: 1; }
  100% { opacity: 0.5; }
}

.code-line {
  min-height: 1.6em;
  transition: all 0.2s ease;
  position: relative;
  white-space: pre;
}

/* Highlighting de palabras clave por CSS */
.code-content {
  /* Keywords */
  --keyword-color: #569cd6;
  /* Strings */
  --string-color: #ce9178;
  /* Comments */
  --comment-color: #6a9955;
  /* Brackets */
  --bracket-color: #ffd700;
}

.code-line.ai-current-line {
  background: rgba(255, 193, 7, 0.15);
  border-left: 3px solid #ffc107;
  animation: pulse 1s infinite;
}

.code-line.ai-completed-line {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
  animation: slideIn 0.3s ease-out;
}

.code-editor-custom {
  display: flex;
  background: #1e1e1e;
  border-radius: 4px;
  overflow: hidden;
  flex: 1;
   min-height: 400px; /* ✅ AGREGAR altura mínima */
  max-height: 600px; /* ✅ AGREGAR altura máxima razonable */
}

.line-number {
  height: 1.6em; /* Misma altura que las líneas de código */
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  white-space: nowrap;
}

/* Hacer que el editor crezca con el contenido */
.code-editor-custom {
  min-height: 300px;
  max-height: calc(100vh - 300px); /* ✅ Usar altura de viewport */
}
  
.line-numbers {
  background: #252526;
  color: #858585;
  padding: 12px 8px;
  font-family: 'JetBrains Mono', Monaco, monospace;
  font-size: 14px;
  line-height: 1.6;
  text-align: right;
  min-width: 50px;
  user-select: none;
  border-right: 1px solid #3c3c3c;
  overflow-y: hidden; /* ✅ AGREGAR esta línea */
  overflow-x: hidden; /* ✅ AGREGAR esta línea */
}

.code-content {
  flex: 1;
  position: relative;
}

.code-content[data-placeholder]:empty::before {
  content: attr(data-placeholder);
  color: #6c757d;
  position: absolute;
  top: 12px;
  left: 12px;
  pointer-events: none;
}

.code-line {
  min-height: 1.6em;
  transition: all 0.2s ease;
  position: relative;
}

.code-line.ai-current-line {
  background: rgba(255, 193, 7, 0.15);
  border-left: 3px solid #ffc107;
  animation: pulse 1s infinite;
}

.code-line.ai-completed-line {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
  animation: slideIn 0.3s ease-out;
}

.code-line {
  transition: all 0.3s ease;
  padding: 2px 5px;
  border-left: 3px solid transparent;
}

.ai-current-line {
  background: rgba(255, 193, 7, 0.08);
  border-left: 2px solid #ffc107;
  box-shadow: inset 0 0 3px rgba(255, 193, 7, 0.1);
}

.ai-completed-line {
  background: rgba(40, 167, 69, 0.06);
  border-left: 2px solid #28a745;
  animation: completedLine 0.4s ease-out;
}

@keyframes completedLine {
  0% { 
    background: rgba(40, 167, 69, 0.15);
    transform: translateX(2px);
  }
  100% { 
    background: rgba(40, 167, 69, 0.06);
    transform: translateX(0);
  }
}

.ai-new-line {
  background: rgba(40, 167, 69, 0.15);
  border-left-color: #28a745;
  animation: slideIn 0.5s ease-out;
}

.ai-completed-line {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
  animation: slideIn 0.3s ease-out;
}

.textarea-enhanced {
  font-family: 'JetBrains Mono', 'Monaco', 'Menlo', monospace !important;
  line-height: 1.6 !important;
  transition: all 0.2s ease;
  caret-color: #007bff; /* Color del cursor */
}

.textarea-enhanced:focus {
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
  border-color: #007bff !important;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes slideIn {
  from { transform: translateX(-5px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.overlay-line.ai-current-line .highlight-bg {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(255, 193, 7, 0.15);
  border-left: 3px solid #ffc107;
  animation: pulse 1s infinite;
}

.overlay-line.ai-completed-line .highlight-bg {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
  animation: slideIn 0.3s ease-out;
}

.textarea-enhanced {
  position: relative;
  z-index: 2;
  background: transparent !important;
}
</style>
`;
  const [files, setFiles] = useState<Array<{filename: string, content: string, type: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isProcessingChat, setIsProcessingChat] = useState(false);
  
  const [unsavedChanges, setUnsavedChanges] = useState<{[filename: string]: string}>({});
  const [chatContext, setChatContext] = useState<any>({});
  const [isThinking, setIsThinking] = useState(false);
  const [currentIntent, setCurrentIntent] = useState<string | null>(null);
  const [highlightedLines, setHighlightedLines] = useState<{[lineNumber: number]: string}>({});
  const [modificationProgress, setModificationProgress] = useState(0);
  const [aiCursor, setAiCursor] = useState<number | null>(null);
  const [codeLines, setCodeLines] = useState<string[]>(['']);
const editorRef = useRef<HTMLTextAreaElement>(null);
  const [previousProjectId, setPreviousProjectId] = useState<string | null>(null);
  const [projectFileCache, setProjectFileCache] = useState<{
  [projectId: string]: {
    files: any[];
    timestamp: number;
  }
}>({});

useEffect(() => {
  if (code) {
    setCodeLines(code.split('\n'));
    // Actualizar editor con texto plano
    /*if (editorRef.current && editorRef.current.textContent !== code) {
      editorRef.current.textContent = code;
    }*/
  }
}, [code]);


   useEffect(() => {
  if (project?.id) {
    // Solo cargar si realmente cambió el proyecto o no hay archivos
    if (!files.length || previousProjectId !== project.id) {
      loadProjectFiles();
      setPreviousProjectId(project.id); // Agregar este estado
    }
  }
  }, [project?.id]);// Cargar archivos reales del proyecto

  // Highlight de sintaxis básico
  /*
const syntaxHighlight = (line: string): string => {
  if (!line) return '\u00A0'; // Espacio para líneas vacías
  
  return line
    .replace(/(import|export|const|let|var|function|return|if|else|interface|type)/g, 
      '<span style="color: #569cd6">$1</span>')
    .replace(/(['"`].*?['"`])/g, 
      '<span style="color: #ce9178">$1</span>')
    .replace(/(\/\/.*)/g, 
      '<span style="color: #6a9955">$1</span>')
    .replace(/(\{|\}|\[|\]|\(|\))/g, 
      '<span style="color: #ffd700">$1</span>');
};*/

// ✅ AGREGAR esta función nueva:
const applyIntelligentModifications = async (newCode: string, explanation: string) => {
  setSaveStatus(`🤖 ${explanation}...`);
  setModificationProgress(0);
  
  // Obtener textarea
  const textarea = editorRef.current as HTMLTextAreaElement;
  if (!textarea) {
    setCode(newCode);
    setCodeLines(newCode.split('\n'));
    return;
  }
  
  // Limpiar y escribir línea por línea
  setCode('');
  setCodeLines(['']);
  
  const lines = newCode.split('\n');
  let accumulatedCode = '';
  
  for (let i = 0; i < lines.length; i++) {
    // Highlight línea actual
    setHighlightedLines({ [i + 1]: 'ai-typing' });
    
    // Agregar línea al código acumulado
    accumulatedCode += (i > 0 ? '\n' : '') + lines[i];
    setCode(accumulatedCode);
    setCodeLines(accumulatedCode.split('\n'));
    
    // Actualizar textarea
    textarea.value = accumulatedCode;
    
    // Progreso
    setModificationProgress(Math.round(((i + 1) / lines.length) * 90));
    
    // Pausa para efecto visual
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Marcar como completada
    setHighlightedLines(prev => ({ 
      ...prev, 
      [i + 1]: 'ai-completed-line' 
    }));
  }
  
  // Finalizar
  setModificationProgress(100);
  setSaveStatus('✅ Modificaciones aplicadas por IA');
  
  // Limpiar highlights
  setTimeout(() => {
    setHighlightedLines({});
    setModificationProgress(0);
  }, 3000);
};

// Highlight de líneas modificadas
const getLineHighlight = (lineNumber: number): string => {
  const highlight = highlightedLines[lineNumber];
  
  // Highlights del sistema (aplicación de cambios)
  if (highlight === 'ai-current-line') return 'ai-current-line';
  if (highlight === 'ai-completed-line') return 'ai-completed-line';
  
  // Highlights del chatbot (modificaciones inteligentes)
  if (highlight === 'ai-typing') return 'ai-typing';
  if (highlight === 'ai-modified') return 'ai-modified';
  if (highlight === 'ai-added') return 'ai-added';
  if (highlight === 'ai-deleted') return 'ai-deleted';
  
  return '';
};

// Manejar input del usuario
const handleCodeInput = (e: React.FormEvent<HTMLDivElement>) => {
  const element = e.currentTarget;
  const content = element.textContent || '';
  
  // Solo actualizar si cambió
  if (content !== code) {
    setCode(content);
    setCodeLines(content.split('\n'));
    
    // Marcar como modificado
    if (activeFile) {
      setUnsavedChanges(prev => ({
        ...prev,
        [activeFile]: content
      }));
    }
  }
};

// Manejar teclas especiales
const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key === 'Tab') {
    e.preventDefault();
    document.execCommand('insertText', false, '  '); // 2 espacios
  }
  
  if (e.key === 'Enter') {
    e.preventDefault();
    document.execCommand('insertText', false, '\n');
  }
};

  // 🧠 FUNCIÓN PRINCIPAL DEL CHATBOT INTELIGENTE
const handleIntelligentChat = async (message: string) => {

  if (!message.trim() || !project?.id) return;
  
  // Agregar mensaje del usuario
  const userMessage = { role: 'user' as const, content: message, timestamp: new Date() };
  setChatMessages(prev => [...prev, userMessage]);
  setChatInput('');
  setIsProcessingChat(true);
  setIsThinking(true);

  try {
    console.log('🤖 Processing intelligent chat:', message);

    // 1. ANÁLISIS DE INTENCIÓN RÁPIDO
    const intentResponse = await fetch('https://backend.anubisai.net/api/component-generator/chat/analyze-intent', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        message: message,
        context: chatContext
      })
    });

    if (intentResponse.ok) {
      const intentResult = await intentResponse.json();
      setCurrentIntent(intentResult.intent.type);
      
      // Mostrar intención detectada
      const intentMessage = { 
        role: 'assistant' as const, 
        content: `🎯 Detecté: ${intentResult.intent.type} (${Math.round(intentResult.intent.confidence * 100)}% confianza)`,
        timestamp: new Date(),
        isSystem: true
      };
      setChatMessages(prev => [...prev, intentMessage]);
    }

    setIsThinking(false);

    // 2. PROCESAMIENTO INTELIGENTE PRINCIPAL
    const response = await fetch('https://backend.anubisai.net/api/component-generator/chat/intelligent', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        message: message,
        projectId: project.id,
        currentFile: activeFile,
        codeContext: code,
        allFiles: files
      })
    });

    const result = await response.json();
    
    if (response.ok && result.success) {
      console.log('🤖 AI Response received:', result);
      
    // 3. APLICAR MODIFICACIONES INTELIGENTES SI HAY CÓDIGO
if (result.success && result.response) {
  // Buscar si hay código modificado en la respuesta
  const codeMatch = result.response.match(/```(?:typescript|tsx|jsx|javascript)\n([\s\S]*?)\n```/);
  
  if (codeMatch && codeMatch[1] && result.confidence > 0.7) {
    const newCode = codeMatch[1];
    await applyIntelligentModifications(
      newCode, 
      'Código modificado por el asistente IA'
    );
  }
}
      
      // 4. MOSTRAR RESPUESTA DEL ASISTENTE
      const assistantMessage = { 
        role: 'assistant' as const, 
        content: generateConversationalMessage(result, { type: result.intent || 'general' }),
        timestamp: new Date(),
        intent: result.intent,
        confidence: result.confidence,
        modifications: result.modifications?.length || 0
      };
      setChatMessages(prev => [...prev, assistantMessage]);
      
    } else {
      throw new Error(result.response || 'Error procesando comando inteligente');
    }
  } catch (error) {
    console.error('❌ Error in intelligent chat:', error);
    const errorMessage = { 
      role: 'assistant' as const, 
      content: `❌ **Error:** ${error instanceof Error ? error.message : 'No pude procesar tu solicitud'}\n\n🤔 Intenta ser más específico o verifica la conexión.`,
      timestamp: new Date(),
      isError: true
    };
    setChatMessages(prev => [...prev, errorMessage]);
  } finally {
    setIsProcessingChat(false);
    setIsThinking(false);
    setCurrentIntent(null);
  }
};

const generateConversationalMessage = (result: any, intent: any): string => {
  // Limpiar respuesta del JSON crudo
  let cleanResponse = result.response;
  
  // Remover bloques JSON del mensaje visible
  cleanResponse = cleanResponse.replace(/```json[\s\S]*?```/g, '').trim();
  cleanResponse = cleanResponse.replace(/\{[\s\S]*?"response"[\s\S]*?\}/g, '').trim();
  
  // Si está vacío, generar mensaje basado en intención
  if (!cleanResponse || cleanResponse.length < 10) {
    const conversationalMessages = {
      create_app: '🚀 He creado la estructura de tu aplicación.',
      modify_code: '✏️ He modificado el código según tu solicitud.',
      add_feature: '➕ He agregado la nueva funcionalidad.',
      fix_bug: '🐛 He corregido los errores encontrados.',
      improve_design: '🎨 He mejorado el diseño del componente.',
      explain_code: '📚 Aquí tienes la explicación del código.'
    };
    
    cleanResponse = conversationalMessages[intent.type] || '🤖 He procesado tu solicitud.';
  }
  
  // Agregar contexto si hay modificaciones
  if (result.modifications && result.modifications.length > 0) {
    cleanResponse += ` Los cambios se están aplicando al código...`;
  }
  
  return cleanResponse;
};

// 🎨 APLICADOR DE MODIFICACIONES CON HIGHLIGHTING
const applyModificationsWithHighlighting = async (modifications: any[]) => {
  for (const mod of modifications) {
    if (mod.filename === activeFile && mod.changes) {
      
      // Obtener código final como texto plano
      const finalCode = mod.changes.map(c => c.newCode).join('\n');
      const lines = finalCode.split('\n');
      
      // Limpiar editor
      setCode('');
      setCodeLines(['']);
      setSaveStatus('🤖 Aplicando cambios...');
      
      // Escribir línea por línea como texto plano
      for (let i = 0; i < lines.length; i++) {
        // Highlight línea actual
        setHighlightedLines({ [i + 1]: 'ai-current-line' });
        
        // Actualizar contenido como texto plano
        const newLines = lines.slice(0, i + 1);
        const newCode = newLines.join('\n');
        setCode(newCode);
        setCodeLines(newLines);
        
        if (editorRef.current) {
  const modifiedLine = editorRef.current.querySelector(`[data-line="${mod.lineNumber}"]`);
  if (modifiedLine) {
    modifiedLine.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
  }
}
        // Actualizar editor directamente con texto plano
        /*if (editorRef.current) {
          editorRef.current.textContent = newCode;
        }*/
        
        // Progreso
        setModificationProgress(Math.round(((i + 1) / lines.length) * 90));
        
        // Pausa para efecto visual
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Marcar como completada
        setHighlightedLines(prev => ({ 
          ...prev, 
          [i + 1]: 'ai-completed-line' 
        }));
      }
      
      // Finalizar
      setModificationProgress(100);
      setSaveStatus('✅ Cambios aplicados');
      
      // Limpiar highlights
      setTimeout(() => {
        setHighlightedLines({});
        setModificationProgress(0);
      }, 3000);
    }
  }
};

  // ✅ AGREGAR esta función nueva:
const formatCodeWithIndentation = (code: string): string => {
  if (!code) return '';
  
  const lines = code.split('\n');
  let indentLevel = 0;
  const formattedLines: string[] = [];
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Conservar líneas vacías
    if (!trimmedLine) {
      formattedLines.push('');
      continue;
    }
    
    // Reducir indentación antes de procesar la línea
    if (trimmedLine.includes('}') || trimmedLine.includes(']') || trimmedLine.endsWith(');')) {
      indentLevel = Math.max(0, indentLevel - 1);
    }
    
    // Aplicar indentación con espacios normales
    const spaces = '  '.repeat(indentLevel); // 2 espacios por nivel
    const formattedLine = spaces + trimmedLine;
    formattedLines.push(formattedLine);
    
    // Aumentar indentación después de procesar la línea
    if (trimmedLine.includes('{') || trimmedLine.includes('[') || trimmedLine.includes('(')) {
      if (!trimmedLine.includes('}') && !trimmedLine.includes(']') && !trimmedLine.includes(')')) {
        indentLevel++;
      }
    }
  }
  
  return formattedLines.join('\n');
};

// ✅ AGREGAR esta función nueva para el efecto typing:
const applyCodeWithTypingEffect = async (targetCode: string, explanation: string) => {
  // Mostrar que estamos escribiendo
  setSaveStatus(`🤖 ${explanation || 'Aplicando cambios'}`);
  setModificationProgress(10);
  
  // Limpiar código actual
  setCode('');
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Escribir código línea por línea como Claude
  const lines = targetCode.split('\n');
  
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    
    // Highlight línea actual
    setHighlightedLines(prev => ({
      ...prev,
      [lineIndex + 1]: 'current'
    }));
    
    // Escribir caracteres de la línea gradualmente
    for (let charIndex = 0; charIndex <= line.length; charIndex++) {
      const partialLine = line.substring(0, charIndex);
      
      setCode(prevCode => {
        const currentLines = prevCode.split('\n');
        currentLines[lineIndex] = partialLine;
        return currentLines.slice(0, lineIndex + 1).join('\n');
      });
      
      // Velocidad de typing (más rápido que Claude pero visible)
      await new Promise(resolve => setTimeout(resolve, 8));
    }
    
    // Marcar línea como completada
    setHighlightedLines(prev => ({
      ...prev,
      [lineIndex + 1]: 'completed'
    }));
    
    // Progreso visual
    const progress = Math.round(((lineIndex + 1) / lines.length) * 80) + 10;
    setModificationProgress(progress);
    
    // Pausa entre líneas (más corta para fluidez)
    await new Promise(resolve => setTimeout(resolve, 30));
  }
  
  // Finalizar efecto
  setModificationProgress(100);
  
  // Limpiar highlights después de 2 segundos
  setTimeout(() => {
    setHighlightedLines({});
    setModificationProgress(0);
    setSaveStatus('✅ Cambios aplicados - Recuerda guardar los cambios');
  }, 2000);
};

// Nueva función para typing effect:
const applyLineWithTypingEffect = async (change: any): Promise<void> => {
  const targetText = change.newCode;
  let currentText = '';
  
  for (let i = 0; i <= targetText.length; i++) {
    currentText = targetText.substring(0, i);
    
    setCode(prevCode => {
      const lines = prevCode.split('\n');
      lines[change.lineNumber - 1] = currentText;
      return lines.join('\n');
    });
    
    await new Promise(resolve => setTimeout(resolve, 20)); // Velocidad typing
  }
};

// 🎯 SUGERENCIAS INTELIGENTES BASADAS EN CONTEXTO
const getContextualSuggestions = () => {
  if (!activeFile) return [];
  
  const fileType = activeFile.split('.').pop();
  const codeContent = code.toLowerCase();
  
  const suggestions: string[] = [];
  
  if (fileType === 'tsx' || fileType === 'jsx') {
    suggestions.push(
      'Agrega validación al formulario',
      'Mejora el diseño del componente',
      'Añade loading state',
      'Implementa error handling'
    );
    
    if (codeContent.includes('usestate')) {
      suggestions.push('Optimiza el estado del componente');
    }
    
    if (!codeContent.includes('proptype') && !codeContent.includes('interface')) {
      suggestions.push('Agrega tipos TypeScript');
    }
  }
  
  if (fileType === 'css') {
    suggestions.push(
      'Haz el diseño más moderno',
      'Agrega animaciones',
      'Mejora la responsividad',
      'Optimiza para dark mode'
    );
  }
  
  return suggestions.slice(0, 6); // Máximo 6 sugerencias
};

// 🎨 COMPONENTE DE LÍNEA CON HIGHLIGHTING
const CodeLineWithHighlight: React.FC<{
  lineNumber: number;
  content: string;
  highlight?: string;
  isAiCursor?: boolean;
}> = ({ lineNumber, content, highlight, isAiCursor }) => {
  const getHighlightClass = () => {
    if (isAiCursor) return 'ai-current-line';
    if (highlight === 'new') return 'ai-new-line';
    if (highlight === 'modified') return 'ai-modified-line';
    if (highlight === 'deleted') return 'ai-deleted-line';
    return '';
  };

  return (
    <div className={`code-line ${getHighlightClass()}`}>
      <span className="line-number">{lineNumber}</span>
      <span className="line-content">{content}</span>
      {isAiCursor && <span className="ai-cursor">🤖</span>}
    </div>
  );
};

// 📊 INDICADOR DE PROGRESO DE MODIFICACIÓN
const ModificationProgress = () => {
  if (modificationProgress === 0) return null;
  
  return (
    <div className="mb-2">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <small className="text-muted">🤖 Aplicando cambios...</small>
        <small className="text-muted">{Math.round(modificationProgress)}%</small>
      </div>
      <ProgressBar 
        now={modificationProgress} 
        variant="info"
        style={{ height: '3px' }}
        animated
      />
    </div>
  );
};

// 🎭 INDICADOR DE PENSAMIENTO DE LA IA
const AIThinkingIndicator = () => {
  if (!isThinking) return null;
  
  return (
    <div className="text-start mb-3">
      <div className="d-inline-block p-3 bg-white border shadow-sm rounded" style={{ maxWidth: '90%' }}>
        <div className="small fw-bold mb-1 text-muted d-flex align-items-center">
          <span className="me-1">🧠</span>
          <span>Analizando intención...</span>
        </div>
        <div className="d-flex align-items-center">
          <Spinner size="sm" className="me-2" />
          <small className="text-muted">
            {currentIntent ? `Detecté: ${currentIntent}` : 'Procesando...'}
          </small>
        </div>
      </div>
    </div>
  );
};

// 💬 MENSAJE MEJORADO DEL CHAT
const ChatMessage = ({ message, index }: { message: any; index: number }) => {
  const isUser = message.role === 'user';
  const isSystem = message.isSystem;
  const isError = message.isError;
  
  const getMessageStyle = () => {
    if (isError) return 'bg-danger text-white';
    if (isSystem) return 'bg-info text-white';
    if (isUser) return 'bg-primary text-white';
    return 'bg-white border shadow-sm';
  };

  const getIcon = () => {
    if (isError) return '❌';
    if (isSystem) return '🎯';
    if (isUser) return '👤';
    return '🤖';
  };

  return (
    <div className={`mb-3 ${isUser ? 'text-end' : 'text-start'}`}>
      <div 
        className={`d-inline-block p-3 rounded ${getMessageStyle()}`}
        style={{ 
          maxWidth: '90%',
          whiteSpace: 'pre-wrap',
          fontSize: '14px',
          lineHeight: '1.4'
        }}
      >
        <div className={`small fw-bold mb-1 d-flex align-items-center ${
          isUser || isSystem || isError ? 'text-light' : 'text-muted'
        }`}>
          <span className="me-1">{getIcon()}</span>
          <span>{isUser ? 'Tú' : isSystem ? 'Sistema' : 'Asistente IA'}</span>
          {message.confidence && (
            <span className="ms-2 opacity-75">
              ({Math.round(message.confidence * 100)}%)
            </span>
          )}
        </div>
        <div>{message.content}</div>
        {message.modifications && message.modifications > 0 && (
          <div className="mt-2 small opacity-75">
            ✨ {message.modifications} modificación(es) aplicada(s)
          </div>
        )}
      </div>
    </div>
  );
};

// 🎯 PANEL DE SUGERENCIAS CONTEXTUALES
const ContextualSuggestions = () => {
  const suggestions = getContextualSuggestions();
  
  if (suggestions.length === 0) return null;
  
  return (
    <div className="mb-3">
      <div className="small text-muted mb-2">💡 Sugerencias para {activeFile}:</div>
      <div className="d-flex flex-wrap gap-1">
        {suggestions.map((suggestion, index) => (
          <Button
            key={index}
            variant="outline-primary"
            size="sm"
            onClick={() => {
  setChatInput(suggestion);
  handleChatCommand(suggestion);
}}
            disabled={isProcessingChat}
            className="small"
            style={{ 
              fontSize: '11px', 
              padding: '4px 8px',
              borderRadius: '12px'
            }}
          >
            {suggestion}
          </Button>
        ))}
      </div>
    </div>
  );
};

// 📊 PANEL DE MÉTRICAS EN TIEMPO REAL
const AIMetricsPanel: React.FC = () => {
  const [metrics, setMetrics] = useState({
    linesModified: 0,
    accuracy: 0,
    responseTime: 0,
    confidence: 0
  });

  return (
    <div className="ai-metrics-panel p-2 bg-body rounded mb-2">
      <div className="small text-muted mb-1">🤖 Métricas de IA:</div>
      <div className="row g-1 small">
        <div className="col-6">
          <div>📝 Líneas: {metrics.linesModified}</div>
          <div>🎯 Precisión: {metrics.accuracy}%</div>
        </div>
        <div className="col-6">
          <div>⚡ Tiempo: {metrics.responseTime}s</div>
          <div>🔍 Confianza: {metrics.confidence}%</div>
        </div>
      </div>
    </div>
  );
};

// 🎨 ESTILOS CSS PARA EL CHATBOT
const chatbotStyles = `
<style>
@keyframes slideInRight {
  from { 
    transform: translateX(100%); 
    opacity: 0; 
  }
  to { 
    transform: translateX(0); 
    opacity: 1; 
  }
}

.chatbot-panel {
  box-shadow: -2px 0 10px rgba(0,0,0,0.1);
}
</style>
`;

// 🔧 HOOK PERSONALIZADO PARA GESTIÓN DEL CHATBOT
const useChatbotIntelligence = (project: Project | null, activeFile: string, code: string) => {
  const [chatState, setChatState] = useState({
    isThinking: false,
    currentIntent: null,
    confidence: 0,
    lastModification: null,
    context: {}
  });

  // Actualizar contexto cuando cambia el archivo o código
  useEffect(() => {
    if (project && activeFile) {
      setChatState(prev => ({
        ...prev,
        context: {
          projectId: project.id,
          currentFile: activeFile,
          codeLength: code.length,
          fileType: activeFile.split('.').pop(),
          timestamp: Date.now()
        }
      }));
    }
  }, [project, activeFile, code]);

  const updateMetrics = (newMetrics: any) => {
    setChatState(prev => ({
      ...prev,
      ...newMetrics
    }));
  };

  return {
    chatState,
    updateMetrics,
    setChatState
  };
};


  const loadProjectFiles = async () => {
  if (!project?.id) return;
  
  // Verificar cache (5 minutos)
  const cached = projectFileCache[project.id];
  const now = Date.now();
  const CACHE_DURATION = 5 * 60 * 1000;
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    console.log('📦 Using cached files');
    setFiles(cached.files);
    if (!activeFile && cached.files.length > 0) {
      setActiveFile(cached.files[0].filename);
      setCode(cached.files[0].content);
    }
    return;
  }
  
    setIsLoading(true);
    try {
      console.log(`🔍 Loading files for project: ${project.id}`);
      
      const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${project.id}/files`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.files && result.files.length > 0) {
          setProjectFileCache(prev => ({
        ...prev,
        [project.id]: {
          files: result.files,
          timestamp: now
        }
      }));
          console.log(`✅ Loaded ${result.files.length} files from API`);
          setFiles(result.files);
          
          // Establecer el primer archivo como activo si no hay uno seleccionado
          if (!activeFile && result.files.length > 0) {
            const firstFile = result.files[0].filename;
            setActiveFile(firstFile);
            setCode(result.files[0].content);
            console.log(`📄 Set active file: ${firstFile}`);
          }
        } else {
          console.log('📝 No files found, initializing project...');
          await initializeProjectFiles();
        }
      } else {
        console.error(`❌ API Error: ${response.status}`);
        await initializeProjectFiles();
      }
    } catch (error) {
      console.error('❌ Error loading files:', error);
      await initializeProjectFiles();
    } finally {
      setIsLoading(false);
    }
  };

  // Inicializar archivos del proyecto si no existen
  const initializeProjectFiles = async () => {
    if (!project?.id) return;
    
    console.log('🔧 Initializing project files...');
    
    try {
      const componentName = project.name.replace(/\s+/g, '');
      const defaultFiles = [
        {
          filename: `${componentName}Component.tsx`,
          content: generateMockCodeForFile(project, `${componentName}Component.tsx`),
          type: 'component'
        },
        {
          filename: `${componentName}Component.css`,
          content: generateMockCodeForFile(project, `${componentName}Component.css`),
          type: 'style'
        },
        {
          filename: 'package.json',
          content: generateMockCodeForFile(project, 'package.json'),
          type: 'config'
        }
      ];

      // Crear cada archivo en el backend
      for (const file of defaultFiles) {
        await saveFileToBackend(file.filename, file.content);
      }
      
      setFiles(defaultFiles);
      if (defaultFiles.length > 0) {
        setActiveFile(defaultFiles[0].filename);
        setCode(defaultFiles[0].content);
      }
      
      setSaveStatus('✅ Archivos del proyecto inicializados');
    } catch (error) {
      console.error('❌ Error initializing files:', error);
      setSaveStatus('❌ Error inicializando archivos');
    }
  };

  // Cambiar archivo activo
  const handleFileChange = (filename: string) => {
    // Guardar cambios no guardados del archivo actual
    if (activeFile && code !== getCurrentFileContent(activeFile)) {
      setUnsavedChanges(prev => ({
        ...prev,
        [activeFile]: code
      }));
    }

    // Cambiar al nuevo archivo
    setActiveFile(filename);
    
    // Cargar contenido del nuevo archivo (con cambios no guardados si existen)
    const newContent = unsavedChanges[filename] || getCurrentFileContent(filename);
const formattedContent = formatCodeWithIndentation(newContent);
setCode(formattedContent);
    if (newContent !== code) {
    setCode(''); // Limpiar primero
    setTimeout(() => {
      typeCodeGradually(newContent);
    }, 100);
  }
    console.log(`📄 Switched to file: ${filename}`);
    setHighlightedLines({}); // Limpiar highlights al cambiar archivo
  setModificationProgress(0); // Limpiar progreso
  };

  const typeCodeGradually = async (targetCode: string) => {
  const lines = targetCode.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    setCode(prev => {
      const currentLines = prev.split('\n');
      currentLines[i] = lines[i];
      return currentLines.slice(0, i + 1).join('\n');
    });
    
    if (i % 5 === 0) { // Pausa cada 5 líneas
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }
};

  // Obtener contenido actual de un archivo
  const getCurrentFileContent = (filename: string): string => {
    const file = files.find(f => f.filename === filename);
    return file?.content || '';
  };

  // Guardar archivo en el backend
  const saveFileToBackend = async (filename: string, content: string) => {
    if (!project?.id) return false;
    
    try {
      const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${project.id}/files`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filename: filename,
          content: content
        })
      });

      const result = await response.json();
      return response.ok && result.success;
    } catch (error) {
      console.error(`❌ Error saving ${filename}:`, error);
      return false;
    }
  };

  // Guardar archivo actual
  const handleSave = async () => {
    if (!project?.id || !activeFile || !code.trim()) return;
    
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      const success = await saveFileToBackend(activeFile, code);
      
      if (success) {
        // Actualizar el contenido del archivo en el estado local
        setFiles(prev => prev.map(file => 
          file.filename === activeFile 
            ? { ...file, content: code }
            : file
        ));
        
        // Limpiar cambios no guardados
        setUnsavedChanges(prev => {
          const updated = { ...prev };
          delete updated[activeFile];
          return updated;
        });
        
        setSaveStatus(`✅ ${activeFile} guardado exitosamente!`);
        console.log(`✅ File saved: ${activeFile}`);
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      setSaveStatus(`❌ Error guardando ${activeFile}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Detectar cambios no guardados
  const hasUnsavedChanges = () => {
    return activeFile && code !== getCurrentFileContent(activeFile);
  };

  // Efecto para limpiar chat con Ctrl+K
useEffect(() => {
  const handleKeyboard = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'k' && showChatbot) {
      e.preventDefault();
      setChatMessages([]);
      setChatInput('');
      setHighlightedLines({});
      setCurrentIntent(null);
    }
  };

  window.addEventListener('keydown', handleKeyboard);
  return () => window.removeEventListener('keydown', handleKeyboard);
}, [showChatbot]);


  // Auto-scroll para chat
useEffect(() => {
  const chatContainer = document.querySelector('.overflow-auto');
  if (chatContainer) {
    chatContainer.scrollTop = chatContainer.scrollHeight;
  }
}, [chatMessages, isThinking]);

// Hook para gestión inteligente del chatbot
const chatbotIntelligence = useChatbotIntelligence(project ?? null, activeFile, code);

  // Generar contenido mock (mismo código que antes)
  const generateMockCodeForFile = (project: Project, filename: string): string => {
    const componentSlug = project.slug || generateSafeSlug(project.name);
    const componentName = componentSlug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('');
    
    switch (filename) {
      case `${componentName}Component.tsx`:
        return `import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import './${componentName}Component.css';

interface ${componentName}Props {
  userId?: string;
}

const ${componentName}: React.FC<${componentName}Props> = ({ userId }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('Loading data...');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="p-4">
      <Row>
        <Col>
          <Card className="${componentName.toLowerCase()}component">
            <Card.Header>
              <h3>${project.name}</h3>
            </Card.Header>
            <Card.Body>
              <p>${project.description}</p>
              <Button variant="primary">
                Acción Principal
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ${componentName};`;

      case `${componentName}Component.css`:
        return `.${componentName.toLowerCase()}component {
  transition: all 0.3s ease;
  padding: 1rem;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.${componentName.toLowerCase()}component:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.${componentName.toLowerCase()}component .btn {
  transition: all 0.2s ease;
  border-radius: 6px;
}`;

      case 'package.json':
        return `{
  "name": "${project.name.toLowerCase().replace(/\s/g, '-')}",
  "version": "1.0.0",
  "description": "${project.description}",
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "react-bootstrap": "^2.7.0"
  }
}`;

      default:
        return `// Archivo: ${filename}\n// Generado para ${project.name}`;
    }
  };

const handleChatCommand = async (message: string) => {
  if (!message.trim()) return;
  
  // Agregar mensaje del usuario
  const userMessage = { role: 'user' as const, content: message };
  setChatMessages(prev => [...prev, userMessage]);
  setChatInput('');
  setIsProcessingChat(true);
  
  try {
    console.log('🤖 Processing chat command:', message);
    
    // Llamar a la API del chatbot CON AUTENTICACIÓN
    const response = await fetch('https://backend.anubisai.net/api/component-generator/chat/process', {
      method: 'POST',
      headers: getAuthHeaders(), // ✅ AGREGAR TOKEN
      body: JSON.stringify({
        message: message,
        currentCode: code,
        projectId: project?.id,
        filename: activeFile
      })
    });

    const result = await response.json();
    
    if (response.ok && result.success) {
      console.log('🤖 AI Response received:', result);
      
      // Interpretar respuesta del LLM e implementar cambios
      const interpretation = interpretAndApplyChanges(result.response, code, activeFile);
      
      // Aplicar cambios al código si los hay
      if (interpretation.hasChanges) {
        setCode(interpretation.newCode);
        setSaveStatus(`🤖 ${interpretation.changeDescription} - Recuerda guardar`);
        console.log('✅ Code changes applied by AI');
        
        // Efecto visual para mostrar que cambió
        const editor = document.querySelector('textarea');
        if (editor) {
          editor.style.border = '2px solid #28a745';
          setTimeout(() => {
            editor.style.border = '';
          }, 2000);
        }
      }
      
      // Extraer mensaje limpio para mostrar
      let displayMessage = result.response;
      try {
        // Buscar JSON en la respuesta
        const codeBlockMatch = result.response.match(/```json\s*\n\s*(\{[\s\S]*?\})\s*\n\s*```/);
        if (codeBlockMatch) {
          const jsonData = JSON.parse(codeBlockMatch[1]);
          displayMessage = jsonData?.response || result.response;
          
          if (jsonData?.explanation && jsonData.explanation !== jsonData.response) {
            displayMessage += '\n\n' + jsonData.explanation;
          }
        }
      } catch (e) {
        // Si falla el parsing, limpiar manualmente
        displayMessage = result.response.replace(/```json[\s\S]*?```/g, '').trim();
      }

      // Mostrar respuesta del asistente
      const finalMessage = interpretation.hasChanges 
        ? `${displayMessage}\n\n✅ **Cambios aplicados:** ${interpretation.changeDescription}`
        : displayMessage;
        
      const assistantMessage = { role: 'assistant' as const, content: finalMessage };
      setChatMessages(prev => [...prev, assistantMessage]);
      
    } else {
      throw new Error(result.error || 'Error procesando comando');
    }
  } catch (error) {
    console.error('❌ Error in chat:', error);
    const errorMessage = { 
      role: 'assistant' as const, 
      content: `❌ **Error:** ${error instanceof Error ? error.message : 'No pude procesar tu solicitud'}\n\nIntenta ser más específico o verifica la conexión.` 
    };
    setChatMessages(prev => [...prev, errorMessage]);
  } finally {
    setIsProcessingChat(false);
  }
};

// 🤖 Función para interpretar respuestas del LLM e implementar cambios
const interpretAndApplyChanges = (llmResponse: string, currentCode: string, filename: string): { newCode: string, hasChanges: boolean, changeDescription: string } => {
  let newCode = currentCode;
  let hasChanges = false;
  let changeDescription = "";

  // PRIMERO: Extraer JSON del markdown si existe
  let actualResponse = llmResponse;
  let jsonData: any = null;

  try {
  // BUSCAR JSON EN CUALQUIER PARTE DE LA RESPUESTA
  const jsonMatch = llmResponse.match(/\{[\s\S]*"hasChanges"\s*:\s*true[\s\S]*\}/);
  if (jsonMatch) {
    console.log('🔍 Found JSON in response:', jsonMatch[0].substring(0, 200));
    jsonData = JSON.parse(jsonMatch[0]);
    actualResponse = jsonData?.response || jsonData?.explanation || llmResponse;
    
    // ✅ BUSCAR CÓDIGO EN DIFERENTES CAMPOS
    let codeToApply: string | null = null;
    
    // 1. Buscar en modifiedCode
    if (jsonData?.modifiedCode) {
      codeToApply = jsonData.modifiedCode;
    }
    
    // 2. Buscar código con múltiples patrones inteligentes
if (!codeToApply) {
  const patterns = [
    /```?typescript\s*\n([\s\S]*?)(?:\n```|$)/,
    /```?tsx\s*\n([\s\S]*?)(?:\n```|$)/,
    /```?javascript\s*\n([\s\S]*?)(?:\n```|$)/,
    /```\s*\n([\s\S]*?)(?:\n```|$)/,
    /(import\s+React[\s\S]*?export\s+default\s+\w+;?)/
  ];
  
  for (const pattern of patterns) {
    const match = llmResponse.match(pattern);
    if (match && match[1]) {
      codeToApply = match[1];
      console.log('🔍 Code extracted with pattern:', pattern.source.substring(0, 50));
      break;
    }
  }
}
    
if (codeToApply && codeToApply.trim().length > 50) {
  console.log('🤖 Processing extracted code...');
  
  // LIMPIAR PREFIJOS INDESEADOS
  codeToApply = codeToApply
    .replace(/^typescript\s*\n?/i, '')
    .replace(/^tsx\s*\n?/i, '')
    .replace(/^javascript\s*\n?/i, '')
    .replace(/^jsx\s*\n?/i, '')
    .trim();
  
  // DETECTAR SI EL CÓDIGO ESTÁ COMPLETO
  const hasImport = codeToApply.includes('import');
  const hasExport = codeToApply.includes('export default') || codeToApply.includes('export {');
  const hasClosingBrace = codeToApply.includes('};') || codeToApply.endsWith('}');
  const isCodeComplete = hasImport && hasExport && hasClosingBrace;
  
  console.log('🔍 Code analysis:', {
    hasImport,
    hasExport, 
    hasClosingBrace,
    isCodeComplete,
    length: codeToApply.length
  });
  
  // PREPARAR DESCRIPCIÓN CON WARNING SI ES NECESARIO
  let finalDescription = jsonData?.explanation || jsonData?.response || "Código modificado por el asistente IA";
  if (!isCodeComplete) {
    finalDescription += " ⚠️ Código puede estar incompleto - verifica el resultado";
    console.log('⚠️ Warning: Code appears incomplete');
  }
  
  console.log('✅ Applying cleaned code to textarea');
  console.log('📋 Final code preview:', codeToApply.substring(0, 100) + '...');
  
  newCode = codeToApply;
  hasChanges = true;
  changeDescription = finalDescription;
  
  return { newCode, hasChanges, changeDescription };
}
    }
  } catch (parseError) {
    console.log('🔍 Could not parse JSON from markdown, using interpretations');
  }
  
  // SI NO HAY CÓDIGO DEL LLM, USAR NUESTRAS INTERPRETACIONES
  const response = actualResponse.toLowerCase();
  console.log('🔍 Processing response with our interpretations:', response);
console.log('🔍 LLM Response length:', llmResponse.length);
console.log('🔍 Has JSON structure:', llmResponse.includes('{') && llmResponse.includes('}'));

  // Detectar tipo de archivo
  const isCSS = filename.endsWith('.css') || filename.endsWith('.scss');
  const isTS = filename.endsWith('.tsx') || filename.endsWith('.ts') || filename.endsWith('.jsx') || filename.endsWith('.js');
  
   // === MEJORAS DE DISEÑO CSS ===
  if ((response.includes('mejora') && response.includes('diseño')) || response.includes('mejorar el diseño')) {
    if (isCSS) {
      console.log('🎨 Applying CSS design improvements');
      
      // Agregar gradientes y efectos modernos
      if (!newCode.includes('background:') && !newCode.includes('background-color:')) {
        newCode = newCode.replace(/(\.[a-zA-Z-]+\s*\{)/, '$1\n  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);');
        hasChanges = true;
      }
      
      // Mejorar sombras existentes o agregar nuevas
      if (newCode.includes('box-shadow')) {
        newCode = newCode.replace(/box-shadow:[^;]+;/g, 'box-shadow: 0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1);');
      } else {
        newCode = newCode.replace(/(\.[a-zA-Z-]+\s*\{[^}]*)(transition:[^;]+;)/, '$1$2\n  box-shadow: 0 4px 20px rgba(0,0,0,0.15);');
      }
      
      // Agregar border-radius moderno
      if (!newCode.includes('border-radius')) {
        newCode = newCode.replace(/(\.[a-zA-Z-]+\s*\{)/, '$1\n  border-radius: 16px;');
      }
      
      // Mejorar transiciones con easing moderno
      if (newCode.includes('transition: all')) {
        newCode = newCode.replace(/transition: all [^;]+;/g, 'transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);');
      }
      
      // Agregar hover effects más modernos
      if (newCode.includes(':hover')) {
        newCode = newCode.replace(/transform: translateY\([^)]+\);/, 'transform: translateY(-8px) scale(1.02);');
      }
      
      changeDescription = "✨ Diseño mejorado: gradientes modernos, sombras suaves, bordes redondeados y transiciones fluidas";
      hasChanges = true;
    } else {
      // Para archivos TS/JS, mejorar estructura
      console.log('⚛️ Applying React design improvements');
      
      if (!newCode.includes('className=')) {
        newCode = newCode.replace(/(<div)/, '$1 className="improved-component"');
        hasChanges = true;
      }
      
      changeDescription = "⚛️ Estructura de componente mejorada";
    }
  }

  // === AGREGAR VARIABLES Y CONSTANTES ===
if (response.includes('variable') || response.includes('constante')) {
  if (isTS && !newCode.includes('const ')) {
    newCode = newCode.replace(/(import.*?;)/, '$1\n\nconst API_URL = "https://api.example.com";\nconst COLORS = {\n  primary: "#007bff",\n  secondary: "#6c757d"\n};');
    changeDescription = "📦 Agregadas: Variables y constantes útiles";
    hasChanges = true;
  }
}

// === MEJORAR RESPONSIVE ===
if (response.includes('responsive') || response.includes('móvil')) {
  if (isCSS) {
    newCode += '\n\n/* Responsive Design */\n@media (max-width: 768px) {\n  .pruebasconceptocomponent {\n    padding: 1rem;\n    margin: 0.5rem;\n  }\n}';
    changeDescription = "📱 Agregado: Diseño responsive para móviles";
    hasChanges = true;
  }
}

// === EFECTOS AVANZADOS ===
if (response.includes('animación') || response.includes('efecto')) {
  if (isCSS) {
    newCode += '\n\n/* Animaciones avanzadas */\n@keyframes slideIn {\n  from { opacity: 0; transform: translateY(20px); }\n  to { opacity: 1; transform: translateY(0); }\n}\n\n.pruebasconceptocomponent {\n  animation: slideIn 0.6s ease-out;\n}';
    changeDescription = "✨ Agregado: Animaciones de entrada suaves";
    hasChanges = true;
  }
}
  // === MEJORAS DE DISEÑO CSS ===
  if (response.includes('mejora') && response.includes('diseño') && isCSS) {
    // Agregar gradientes y efectos modernos
    if (!newCode.includes('background:') && !newCode.includes('background-color:')) {
      newCode = newCode.replace(/(\.[a-zA-Z-]+\s*\{)/, '$1\n  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);');
      hasChanges = true;
    }
    
    // Mejorar sombras
    if (newCode.includes('box-shadow')) {
      newCode = newCode.replace(/box-shadow:[^;]+;/, 'box-shadow: 0 8px 32px rgba(0,0,0,0.2);');
    } else {
      newCode = newCode.replace(/(\.[a-zA-Z-]+\s*\{[^}]*)(transition:[^;]+;)/, '$1$2\n  box-shadow: 0 4px 20px rgba(0,0,0,0.1);');
    }
    
    // Agregar border-radius si no existe
    if (!newCode.includes('border-radius')) {
      newCode = newCode.replace(/(\.[a-zA-Z-]+\s*\{)/, '$1\n  border-radius: 12px;');
    }
    
    // Mejorar transiciones
    if (newCode.includes('transition: all')) {
      newCode = newCode.replace(/transition: all [^;]+;/, 'transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);');
    }
    
    changeDescription = "✨ Diseño mejorado: gradientes, sombras suaves, bordes redondeados y transiciones fluidas";
    hasChanges = true;
  }
  
  // === CORRECCIÓN DE ERRORES ===
  if (response.includes('corrige') || response.includes('error')) {
    // Corregir sintaxis común
    newCode = newCode.replace(/([^:])(\s*\{)/g, '$1$2'); // Espacios antes de llaves
    newCode = newCode.replace(/;\s*}/g, '\n}'); // Nueva línea antes de cerrar
    newCode = newCode.replace(/([^;])\s*}/g, '$1;\n}'); // Agregar punto y coma faltante
    
    if (isTS) {
      // Agregar tipos faltantes
      if (newCode.includes('useState(') && !newCode.includes(': React.')) {
        newCode = newCode.replace(/const\s+(\w+)\s*=\s*useState/, 'const $1: [any, React.Dispatch<React.SetStateAction<any>>] = useState');
      }
      
      // Agregar export si falta
      if (!newCode.includes('export') && newCode.includes('const ')) {
        newCode = newCode.replace(/const\s+(\w+)\s*=/, 'export const $1 =');
      }
    }
    
    changeDescription = "🔧 Errores corregidos: sintaxis, tipos TypeScript y exports";
    hasChanges = true;
  }
  
  // === AGREGAR FUNCIONALIDADES ===
  if (response.includes('agrega') || response.includes('añade') || response.includes('nueva')) {
    if (isTS && response.includes('button')) {
      // Agregar botón si no existe
      if (!newCode.includes('<Button') && !newCode.includes('<button')) {
        newCode = newCode.replace(/(<\/div>)(\s*<\/.*>)$/, `  <Button variant="primary" className="mt-3">\n    Nueva Acción\n  </Button>\n$1$2`);
        changeDescription = "➕ Agregado: Botón de acción";
        hasChanges = true;
      }
    }
    
    if (isTS && response.includes('estado')) {
      // Agregar useState si no existe
      if (!newCode.includes('useState') && newCode.includes('React')) {
        newCode = newCode.replace(/(import.*from\s+['"]react['"];?)/, `$1\nconst [newState, setNewState] = useState(false);`);
        changeDescription = "➕ Agregado: Estado con useState";
        hasChanges = true;
      }
    }
  }
  
  // === OPTIMIZACIONES ===
  if (response.includes('optimiza') || response.includes('mejora el código')) {
    if (isCSS) {
      // Combinar selectores similares
      // Agregar variables CSS
      if (!newCode.includes(':root')) {
        newCode = `:root {\n  --primary-color: #007bff;\n  --shadow-light: 0 2px 10px rgba(0,0,0,0.1);\n  --border-radius: 8px;\n}\n\n` + newCode;
        hasChanges = true;
      }
    }
    
    if (isTS) {
      // Agregar comentarios útiles
      newCode = '// Componente optimizado automáticamente\n' + newCode;
      hasChanges = true;
    }
    
    changeDescription = "⚡ Código optimizado: variables CSS y comentarios";
  }
  
  // === ESTILOS ESPECÍFICOS ===
  if (response.includes('color') || response.includes('colores')) {
    if (isCSS) {
      // Agregar esquema de colores moderno
      newCode = newCode.replace(/color:\s*[^;]+;/, 'color: #2d3748;');
      newCode = newCode.replace(/background-color:\s*[^;]+;/, 'background-color: #f7fafc;');
      changeDescription = "🎨 Colores actualizados con paleta moderna";
      hasChanges = true;
    }
  }
  
   console.log('🔍 Changes applied:', hasChanges, changeDescription);
  return { newCode, hasChanges, changeDescription };
};

// ✅ AGREGAR estas funciones auxiliares:
const getHighlightColor = (highlightType: string): string => {
  switch (highlightType) {
    case 'ai-typing': return 'rgba(0, 123, 255, 0.15)';
    case 'ai-modified': return 'rgba(138, 43, 226, 0.15)';
    case 'ai-added': return 'rgba(34, 197, 94, 0.15)';
    case 'ai-deleted': return 'rgba(239, 68, 68, 0.15)';
    case 'ai-current-line': return 'rgba(255, 193, 7, 0.15)';
    case 'ai-completed-line': return 'rgba(40, 167, 69, 0.1)';
    default: return 'transparent';
  }
};

const getHighlightBorderColor = (highlightType: string): string => {
  switch (highlightType) {
    case 'ai-typing': return '#007bff';
    case 'ai-modified': return '#8a2be2';
    case 'ai-added': return '#22c55e';
    case 'ai-deleted': return '#ef4444';
    case 'ai-current-line': return '#ffc107';
    case 'ai-completed-line': return '#28a745';
    default: return 'transparent';
  }
};

const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
  const lineNumbersDiv = document.querySelector('.line-numbers') as HTMLElement;
  if (lineNumbersDiv) {
    lineNumbersDiv.scrollTop = e.currentTarget.scrollTop;
  }
};

if (!project) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <div className="fs-1 mb-3">💻</div>
          <h5>No hay proyecto seleccionado</h5>
          <p className="text-body-secondary">
            Selecciona una aplicación desde la pestaña "Mis Aplicaciones" para comenzar a editarla
          </p>
        </Card.Body>
      </Card>
    );
  }

  //const files = ['App.tsx', 'components/DataTable.tsx', 'hooks/useData.ts', 'styles/main.css', 'package.json'];

  return (
    <div className="d-flex" style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Panel Principal de Código */}
      <div dangerouslySetInnerHTML={{ __html: editorStyles }} />
      <div className={`d-flex flex-column ${showChatbot ? 'flex-shrink-1' : 'flex-grow-1'}`} 
     style={{ transition: 'all 0.3s ease', width: showChatbot ? 'calc(100% - 450px)' : '100%' }}>
        {/* File Tabs */}
        <Card className="mb-3">
          <Card.Header className="pb-0">
            <Nav variant="tabs" className="border-0">
              {files.map(file => (
                <Nav.Item key={file.filename}>
                  <Nav.Link 
                    active={activeFile === file.filename}
                    onClick={() => handleFileChange(file.filename)}
                    className="border-0 d-flex align-items-center"
                  >
                    📄 {file.filename}
                    {unsavedChanges[file.filename] && (
                      <span className="text-warning ms-1" title="Cambios no guardados">●</span>
                    )}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Card.Header>
        </Card>

        {/* Toolbar */}
        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <h6 className="mb-0">{project.name}</h6>
                <Badge bg="primary">{project.type}</Badge>
                <Badge bg={project.status === 'deployed' ? 'success' : 'warning'}>
                  {project.status}
                </Badge>
                {hasUnsavedChanges() && (
                  <Badge bg="warning" className="text-dark">
                    Sin guardar
                  </Badge>
                )}
                {isLoading && <Spinner size="sm" />}
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Row>
                  <Col>
                  <Button 
    variant="outline-secondary" 
    size="sm"
    onClick={() => setShowConfigModal(true)}
  >
    <FontAwesomeIcon icon={faCogs} className="me-1" />
    Configurar
  </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(code)}
                >
                  <FontAwesomeIcon icon={faCopy} className="me-1" />
                  Copiar
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges()}
                >
                  {isSaving ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-1" />
                      Guardar
                    </>
                  )}
                </Button>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => alert('Deploy iniciado')}
                >
                  <FontAwesomeIcon icon={faRocket} className="me-1" />
                  Deploy
                </Button>
                <Button 
  variant={showChatbot ? "success" : "outline-primary"} 
  size="sm"
  onClick={() => setShowChatbot(!showChatbot)}
  className="ms-2"
    title="Asistente IA Inteligente"
    style={{
      background: showChatbot 
        ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      color: 'white'
    }}
  >
    <FontAwesomeIcon icon={faRocket} className="me-1" />
    🧠 IA
  </Button>
  </Col>
  </Row>
              </div>
            </div>
            
            {saveStatus && (
              <Alert 
                variant={saveStatus.includes('✅') ? 'success' : 'danger'} 
                className="mt-2 mb-0"
              >
                <small>{saveStatus}</small>
              </Alert>
            )}
          </Card.Body>
        </Card>

        {/* Editor de Código */}
<Card.Body className="p-0 d-flex flex-column flex-grow-1">
  {/* ✅ NUEVO: Editor con textarea y overlay de números */}
  <div className="code-editor-wrapper" style={{ position: 'relative', flex: 1, display: 'flex' }}>
    
    {/* Overlay de números de línea */}
    <div 
      className="line-numbers-overlay"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '50px',
        background: '#252526',
        color: '#858585',
        fontSize: '14px',
        fontFamily: 'JetBrains Mono, Monaco, monospace',
        lineHeight: '1.6',
        textAlign: 'right',
        padding: '12px 8px',
        borderRight: '1px solid #3c3c3c',
        userSelect: 'none',
        overflow: 'hidden',
        zIndex: 2,
        pointerEvents: 'none'
      }}
    >
      {codeLines.map((_, index) => (
        <div 
          key={index} 
          style={{ 
            height: '1.6em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '8px'
          }}
        >
          {index + 1}
        </div>
      ))}
    </div>

    {/* Textarea principal */}
    <textarea
      key={activeFile}
      ref={editorRef}
      className="code-textarea"
      value={code}
      onChange={(e) => {
        setCode(e.target.value);
        setCodeLines(e.target.value.split('\n'));
        
        // Marcar como modificado
        if (activeFile) {
          setUnsavedChanges(prev => ({
            ...prev,
            [activeFile]: e.target.value
          }));
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          const start = e.currentTarget.selectionStart;
          const end = e.currentTarget.selectionEnd;
          const newValue = code.substring(0, start) + '  ' + code.substring(end);
          setCode(newValue);
          setCodeLines(newValue.split('\n'));
          setTimeout(() => {
            e.currentTarget.selectionStart = e.currentTarget.selectionEnd = start + 2;
          }, 0);
        }
      }}
      onScroll={(e) => {
        // Sincronizar scroll con números de línea
        const lineNumbersDiv = document.querySelector('.line-numbers-overlay') as HTMLElement;
        if (lineNumbersDiv) {
          lineNumbersDiv.scrollTop = e.currentTarget.scrollTop;
        }
      }}
      placeholder="// Tu código aparecerá aquí..."
      disabled={isLoading}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '400px',
        backgroundColor: '#1e1e1e',
        color: '#d4d4d4',
        fontFamily: 'JetBrains Mono, Monaco, monospace',
        fontSize: '14px',
        lineHeight: '1.6',
        border: 'none',
        outline: 'none',
        resize: 'none',
        padding: '12px 12px 12px 60px', // Espacio para números
        whiteSpace: 'pre',
        overflow: 'auto'
      }}
    />

    {/* Overlay para highlights de IA */}
    <div 
      className="highlights-overlay"
      style={{
        position: 'absolute',
        left: '50px',
        top: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        padding: '12px',
        overflow: 'hidden',
        zIndex: 1
      }}
    >
      {codeLines.map((line, index) => (
        <div 
          key={`highlight-${activeFile}-${index}`}
          className={`highlight-line ${getLineHighlight(index + 1)}`}
          style={{
            height: '1.6em',
            lineHeight: '1.6',
            fontSize: '14px',
            fontFamily: 'JetBrains Mono, Monaco, monospace',
            position: 'relative'
          }}
        >
          {highlightedLines[index + 1] && (
            <div 
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                background: getHighlightColor(highlightedLines[index + 1]),
                borderLeft: `3px solid ${getHighlightBorderColor(highlightedLines[index + 1])}`,
                opacity: 0.3
              }}
            />
          )}
        </div>
      ))}
    </div>
  </div>
</Card.Body>
      </div>
{/* 🤖 PANEL LATERAL DEL CHATBOT COMPLETO */}
{showChatbot && (
  <>
    <div dangerouslySetInnerHTML={{ __html: chatbotStyles }} />
    
    <div 
      className="chatbot-panel border-start"
      style={{ 
        width: '450px', 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        animation: 'slideInRight 0.3s ease-out'
      }}
    >
      {/* Header Inteligente */}
      <div className="p-3 text-body bg-body" style={{
        //background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div 
              className="me-2" 
              style={{ 
                width: '10px', 
                height: '10px', 
                borderRadius: '50%', 
                backgroundColor: isProcessingChat ? '#fbbf24' : '#22c55e',
                animation: isProcessingChat ? 'pulse 1s infinite' : 'none'
              }}
            />
            <div>
              <h6 className="mb-0">🧠 Asistente IA Inteligente</h6>
              <small className="opacity-75">
                {isProcessingChat ? 'Procesando...' : 
                 currentIntent ? `Modo: ${currentIntent}` : 
                 'Listo para ayudar'}
              </small>
            </div>
          </div>
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={() => setShowChatbot(false)}
            style={{ borderColor: 'rgba(255,255,255,0.3)' }}
          >
            ✕
          </Button>
        </div>
      </div>

      {/* Progreso de modificación */}
      {modificationProgress > 0 && (
        <div className="p-3 bg-body border-bottom">
          <ModificationProgress />
        </div>
      )}

      {/* Métricas de IA */}
      <div className="p-3 bg-body border-bottom">
        <AIMetricsPanel />
      </div>

      {/* Mensajes del Chat */}
      <div 
        className="flex-grow-1 overflow-auto bg-body p-3"
        style={{ maxHeight: 'calc(100vh - 350px)' }}
      >
        {chatMessages.length === 0 ? (
          <div className="text-center text-muted bg-body">
            <div className="mb-4" style={{ fontSize: '3rem' }}>🧠</div>
            <h6 className="fw-bold text-primary mb-3">¡Asistente IA Avanzado!</h6>
            <p className="small mb-3">Puedo entender y ejecutar tareas complejas:</p>
            
            <div className="text-start small mb-4">
              <div className="mb-3 p-3 bg-primary bg-opacity-10 rounded">
                <div className="fw-bold text-primary mb-2">🚀 Crear Aplicaciones:</div>
                <div className="mb-1">"Crea una app de inventario"</div>
                <div>"Necesito un CRM para clientes"</div>
              </div>
              
              <div className="mb-3 p-3 bg-success bg-opacity-10 rounded">
                <div className="fw-bold text-success mb-2">✏️ Modificar Código:</div>
                <div className="mb-1">"Agrega validación al formulario"</div>
                <div>"Mejora el diseño del botón"</div>
              </div>
              
              <div className="mb-3 p-3 bg-warning bg-opacity-10 rounded">
                <div className="fw-bold text-warning mb-2">🎨 Mejorar Diseño:</div>
                <div className="mb-1">"Haz que se vea más moderno"</div>
                <div>"Cambia los colores a azul"</div>
              </div>
            </div>
            
            <div className="p-3 bg-info bg-opacity-10 rounded">
              <small className="text-info">
                💡 <strong>Tip:</strong> Describe claramente lo que necesitas
              </small>
            </div>
          </div>
        ) : (
          <>
            <AIThinkingIndicator />
            {chatMessages.map((msg, index) => (
              <ChatMessage key={index} message={msg} index={index} />
            ))}
          </>
        )}
      </div>
      
      {/* Sugerencias Contextuales */}
      <div className="p-3 bg-body border-top">
        <ContextualSuggestions />
      </div>
      
      {/* Input Avanzado del Chat */}
      <div className="p-3 bg-body border-top">
        <div className="mb-3">
          <div className="input-group">
            <Form.Control
              type="text"
              placeholder={
                activeFile 
                  ? `Dime qué modificar en ${activeFile}...`
                  : "Describe la aplicación que necesitas..."
              }
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isProcessingChat && chatInput.trim()) {
                  handleChatCommand(chatInput);
                }
              }}
              disabled={isProcessingChat}
              className="chat-input-enhanced"
              style={{ fontSize: '14px' }}
            />
            <Button 
              variant="primary"
              onClick={() => handleChatCommand(chatInput)}
              disabled={!chatInput.trim() || isProcessingChat}
              style={{ 
                minWidth: '50px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
            >
              {isProcessingChat ? (
                <Spinner size="sm" />
              ) : (
                <span style={{ fontSize: '16px' }}>🚀</span>
              )}
            </Button>
          </div>
        </div>
        
        {/* Comandos Rápidos */}
        <div className="d-flex flex-wrap gap-1 mb-2">
          {[
            { emoji: '🚀', text: 'Crear app', command: 'Crea una aplicación de ' },
            { emoji: '✏️', text: 'Modificar', command: 'Modifica este código para ' },
            { emoji: '🎨', text: 'Diseño', command: 'Mejora el diseño ' },
            { emoji: '🐛', text: 'Corregir', command: 'Corrige los errores' },
            { emoji: '📚', text: 'Explicar', command: 'Explica este código' },
            { emoji: '⚡', text: 'Optimizar', command: 'Optimiza el rendimiento' }
          ].map((item, index) => (
            <Button
              key={index}
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setChatInput(item.command);
              }}
              disabled={isProcessingChat}
              style={{ 
                fontSize: '10px', 
                padding: '3px 8px',
                borderRadius: '15px'
              }}
            >
              {item.emoji} {item.text}
            </Button>
          ))}
        </div>
        
        {/* Status Inteligente */}
        <div className="d-flex align-items-center justify-content-between">
          <small className="text-muted">
            {isProcessingChat ? (
              <span className="text-warning">
                <Spinner size="sm" className="me-1" />
                🧠 Procesando con IA...
              </span>
            ) : currentIntent ? (
              <span className="text-info">
                🎯 Modo: {currentIntent}
              </span>
            ) : chatMessages.length > 0 ? (
              <span className="text-success">✅ Listo para la siguiente tarea</span>
            ) : (
              <span>💭 Describe lo que necesitas</span>
            )}
          </small>
          
          <small className="text-muted">
            <kbd>Enter</kbd> enviar • <kbd>Ctrl+K</kbd> limpiar
          </small>
        </div>
      </div>
    </div>
  </>
)}
{/* Modal de configuración */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Configuración de la Aplicación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>🔧 Configuración Avanzada</strong><br />
            Personaliza el comportamiento y conexiones de datos de tu aplicación.
          </Alert>
          
          <div>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Fuente de Datos</label>
                  <select className="form-select">
                    <option>📝 Entrada Manual</option>
                    <option>📤 Subida de Archivos</option>
                    <option>🔗 API Externa</option>
                    <option>🗄️ Base de Datos</option>
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Tema Visual</label>
                  <select className="form-select">
                    <option>🌞 Claro</option>
                    <option>🌙 Oscuro</option>
                    <option>🎨 Automático</option>
                  </select>
                </div>
              </Col>
            </Row>
            <div className="mb-3">
              <label className="form-label">URL de Deploy (opcional)</label>
              <input 
                type="url" 
                className="form-control"
                placeholder="https://mi-app.anubisai.net"
                defaultValue={project.deployUrl || ''}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary">
            Aplicar Cambios
          </Button>
        </Modal.Footer>
      </Modal>
  </div>
);
};

// Preview Tab - FUNCIONAL CON INVALIDACIÓN DE CACHÉ
const PreviewTab: React.FC<{ project?: Project | null }> = ({ project }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewKey, setPreviewKey] = useState(0); // Para forzar recarga
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
const { user } = useUserContext();

  useEffect(() => {
    if (project?.id) {
      loadPreview();
    }
  }, [project?.id]);

// Cargar preview con autenticación
const loadPreview = async () => {
  if (!project?.id) return;
  
  setIsLoading(true);
  
  try {
    // HACER REQUEST CON AUTENTICACIÓN PARA GENERAR EL HTML
    const response = await fetch(`https://backend.anubisai.net/api/component-generator/preview/${project.id}`, {
      method: 'GET',
      headers: getAuthHeaders() // ✅ AGREGAR TOKEN AQUÍ
    });

    if (response.ok) {
      // Convertir respuesta a blob para crear URL local
      const htmlBlob = await response.blob();
      const blobUrl = URL.createObjectURL(htmlBlob);
      
      setPreviewUrl(blobUrl);
      setPreviewKey(prev => prev + 1);
      setLastRefresh(new Date());
      
      console.log(`✅ Preview loaded for project: ${project.id}`);
    } else {
      console.error(`❌ Preview API Error: ${response.status}`);
      throw new Error(`Error ${response.status}: No autorizado`);
    }
  } catch (error) {
    console.error('❌ Error loading preview:', error);
    setDeployStatus(`❌ Error cargando preview: ${error.message}`);
  } finally {
    setIsLoading(false);
  }
};

  // FUNCIÓN RESTAURADA: Deploy funcional con API
  const handleDeploy = async () => {
    if (!project?.id) return;
    
    setIsDeploying(true);
    setDeployStatus(null);
    
    try {
      const response = await fetch('https://backend.anubisai.net/api/component-generator/deploy', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          projectId: project.id,
          deployPath: `/tmp/database/${user._id}/deployments/${project.id}`,
          runBuild: true
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setDeployStatus('✅ Deploy completado exitosamente!');
        setTimeout(() => {
          loadPreview();
        }, 1000);
      } else {
        console.error('Deploy API Error:', response.status, result);
        throw new Error(result.error || 'Deploy failed');
      }
      
    } catch (error) {
      console.error('Deploy error:', error);
      setDeployStatus(`❌ Error en deploy: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      
      // Fallback para desarrollo
      setTimeout(() => {
        setDeployStatus('✅ Deploy completado (modo respaldo)');
      }, 2000);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleRefresh = () => {
    loadPreview();
  };

  // Abrir preview en nueva ventana
  const handleOpenInNewWindow = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'width=1200,height=800');
    }
  };

  if (!project) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <div className="fs-1 mb-3">👁️</div>
          <h5>No hay proyecto seleccionado</h5>
          <p className="text-body-secondary">
            Selecciona una aplicación para ver su vista previa
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      {/* Preview Controls */}
      <Card className="mb-3">
  <Card.Body className="py-2">
    <div className="d-flex align-items-center justify-content-between" 
         style={{ width: '100%', overflow: 'hidden' }}>
            <div className="d-flex align-items-center gap-3">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faEye} className="me-2" />
                Vista Previa - {project.name}
              </h6>
              {lastRefresh && (
                <small className="text-muted">
                  Actualizado: {lastRefresh.toLocaleTimeString()}
                </small>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                title="Actualizar preview"
              >
                <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleOpenInNewWindow}
                disabled={!previewUrl || isLoading}
                title="Abrir en nueva ventana"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Button>
              <Button 
                variant="success" 
                size="sm"
                onClick={handleDeploy}
                disabled={isDeploying}
              >
                {isDeploying ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Desplegando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRocket} className="me-1" />
                    Deploy
                  </>
                )}
              </Button>
            </div>
          </div>

          {deployStatus && (
            <Alert 
              variant={deployStatus.includes('✅') ? 'success' : 'danger'} 
              className="mt-2 mb-0"
              dismissible 
              onClose={() => setDeployStatus(null)}
            >
              <small>{deployStatus}</small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Preview Area */}
      <Card>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
              <div className="text-center">
                <Spinner className="mb-3" />
                <p className="text-body-secondary mb-0">Cargando vista previa actualizada...</p>
              </div>
            </div>
          ) : previewUrl ? (
            <div style={{ height: '500px', position: 'relative' }}>
              <iframe
                key={previewKey} // Forzar recarga cuando cambia
                src={previewUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
                title={`Preview de ${project.name}`}
                onLoad={() => console.log(`✅ Preview loaded for project: ${project.id}`)}
                onError={() => console.error(`❌ Preview failed for project: ${project.id}`)}
              />
              
              {/* Overlay de carga */}
              <div 
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light"
                style={{ 
                  opacity: isLoading ? 1 : 0, 
                  transition: 'opacity 0.3s',
                  pointerEvents: isLoading ? 'auto' : 'none',
                  borderRadius: '8px'
                }}
              >
                <div className="text-center">
                  <Spinner className="mb-2" />
                  <p className="text-muted mb-0">Cargando...</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
              <div className="text-center">
                <div className="fs-1 mb-3">🔧</div>
                <h6>Preview no disponible</h6>
                <p className="text-muted mb-3">Haz clic en "Actualizar" para generar la vista previa</p>
                <Button variant="primary" onClick={handleRefresh}>
                  <FontAwesomeIcon icon={faRefresh} className="me-2" />
                  Generar Preview
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
        <Card.Footer className="text-muted">
          <small>
            <FontAwesomeIcon icon={faEye} className="me-1" />
            Vista previa en tiempo real • {project.type} • {project.complexity}
            {previewUrl && (
              <span className="ms-3">
                <FontAwesomeIcon icon={faLink} className="me-1" />
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                  Abrir en nueva pestaña
                </a>
              </span>
            )}
          </small>
        </Card.Footer>
      </Card>

      {/* Información adicional del proyecto */}
      <Row className="mt-3">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Información del Proyecto</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <strong>ID:</strong> <code>{project.id}</code>
              </div>
              <div className="mb-2">
                <strong>Creado:</strong> {project.createdAt}
              </div>
              <div className="mb-2">
                <strong>Última modificación:</strong> {project.lastModified}
              </div>
              <div className="mb-2">
                <strong>Estado:</strong> 
                <Badge bg={
                  project.status === 'deployed' ? 'success' : 
                  project.status === 'testing' ? 'warning' : 'info'
                } className="ms-2">
                  {project.status}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Acciones Rápidas</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button variant="outline-primary" size="sm" onClick={handleRefresh}>
                  <FontAwesomeIcon icon={faSync} className="me-2" />
                  Actualizar Preview
                </Button>
                <Button variant="outline-success" size="sm">
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Descargar Código
                </Button>
                <Button variant="outline-info" size="sm">
                  <FontAwesomeIcon icon={faCogs} className="me-2" />
                  Configurar
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

  const handleStatusChange = async (projectId: string, newStatus: Project['status']) => {
    try {
      // Actualizar estado en la API
      const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${projectId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setProjects(prev => 
          prev.map(p => 
            p.id === projectId 
              ? { ...p, status: newStatus, lastModified: new Date().toISOString().split('T')[0] }
              : p
          )
        );
      } else {
        console.error('Status update API Error:', response.status);
        // Actualizar localmente como fallback
        setProjects(prev => 
          prev.map(p => 
            p.id === projectId 
              ? { ...p, status: newStatus, lastModified: new Date().toISOString().split('T')[0] }
              : p
          )
        );
      }
    } catch (error) {
      console.error('Error updating status:', error);
      // Actualizar localmente como fallback
      setProjects(prev => 
        prev.map(p => 
          p.id === projectId 
            ? { ...p, status: newStatus, lastModified: new Date().toISOString().split('T')[0] }
            : p
        )
      );
    }
  };

  return (
    <Container fluid className="px-0">
      <Row className="g-0">
        <Col>
          {/* Header mejorado */}
          <div className="bg-gradient-primary text-white p-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h1 className="h3 mb-1">
                  🚀 Anubis AI App Generator
                </h1>
                <p className="mb-0 opacity-90 text-body">
                  Crea aplicaciones completas sin código - Describe tu idea y nosotros la construimos
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Button 
                  variant="light" 
                  size="sm"
                  onClick={loadProjects}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
                </Button>
                <Button 
                  variant="success"
                  onClick={() => setActiveTab('app-generator')}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Nueva Aplicación
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs mejorada */}
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
            <div className="bg-body border-bottom">
              <Nav variant="tabs" className="border-0 px-4">
                <Nav.Item>
                  <Nav.Link eventKey="overview" className="border-0 py-3">
                    <FontAwesomeIcon icon={faChartBar} className="me-2" />
                    Overview
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="app-generator" className="border-0 py-3">
                    <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />
                    Generador IA
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="projects" className="border-0 py-3">
                    <FontAwesomeIcon icon={faFolderTree} className="me-2" />
                    Mis Aplicaciones ({projects.length})
                  </Nav.Link>
                </Nav.Item>
                {selectedProject && (
                  <>
                    <Nav.Item>
                      <Nav.Link eventKey="code-editor" className="border-0 py-3">
                        <FontAwesomeIcon icon={faCode} className="me-2" />
                        {selectedProject.name}
                        <Badge bg="secondary" className="ms-2">{selectedProject.status}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="preview" className="border-0 py-3">
                        <FontAwesomeIcon icon={faEye} className="me-2" />
                        Vista Previa
                      </Nav.Link>
                    </Nav.Item>
                  </>
                )}
              </Nav>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              <Tab.Content>
                <Tab.Pane eventKey="overview">
                  <OverviewTab projects={projects} />
                </Tab.Pane>
                
                <Tab.Pane eventKey="app-generator">
                  <AppGeneratorTab 
  onProjectGenerated={loadProjects}
  generationStep={generationStep}
  setGenerationStep={setGenerationStep}
  isGenerating={isGenerating}
  setIsGenerating={setIsGenerating}
  generationProgress={generationProgress}
  setGenerationProgress={setGenerationProgress}
  setActiveTab={setActiveTab}
  setSelectedProject={setSelectedProject}
/>
                </Tab.Pane>
                
                <Tab.Pane eventKey="projects">
                  <ProjectsTab 
                    projects={projects} 
                    onProjectSelect={handleProjectSelect}
                    selectedProject={selectedProject}
                    onDeleteProject={handleDeleteProject}
                    onStatusChange={handleStatusChange}
                    setProjects={setProjects}
                  />
                </Tab.Pane>
                
                <Tab.Pane eventKey="code-editor">
                  <CodeEditorTab project={selectedProject} />
                </Tab.Pane>
                
                <Tab.Pane eventKey="preview">
                  <PreviewTab project={selectedProject} />
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        </Col>
      </Row>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            ¿Estás seguro de que quieres eliminar la aplicación <strong>{projectToDelete?.name}</strong>?
          </Alert>
          <p>Esta acción no se puede deshacer. Se perderán todos los archivos y configuraciones.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Eliminar Definitivamente
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// Overview Tab - Estadísticas y métricas
const OverviewTab: React.FC<{ projects: Project[] }> = ({ projects }) => {
  const stats = {
    totalApps: projects.length,
    deployedApps: projects.filter(p => p.status === 'deployed').length,
    draftApps: projects.filter(p => p.status === 'draft').length,
    testingApps: projects.filter(p => p.status === 'testing').length
  };

  const categoryDistribution = {
    dashboard: projects.filter(p => p.category === 'dashboard').length,
    management: projects.filter(p => p.category === 'management').length,
    communication: projects.filter(p => p.category === 'communication').length,
    utility: projects.filter(p => p.category === 'utility').length,
    custom: projects.filter(p => p.category === 'custom').length
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Dashboard de Aplicaciones</h4>
        <Badge bg="primary" className="fs-6">{stats.totalApps} aplicaciones creadas</Badge>
      </div>

      {/* Estadísticas principales */}
      <Row className="mb-4">
        <Col sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100 border-primary">
            <Card.Body>
              <div className="fs-1 mb-2">📱</div>
              <h3 className="text-primary fw-bold mb-1">{stats.totalApps}</h3>
              <small className="text-body-secondary text-uppercase fw-semibold">
                Total Aplicaciones
              </small>
            </Card.Body>
          </Card>
        </Col>
        
        <Col sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100 border-success">
            <Card.Body>
              <div className="fs-1 mb-2">🚀</div>
              <h3 className="text-success fw-bold mb-1">{stats.deployedApps}</h3>
              <small className="text-body-secondary text-uppercase fw-semibold">
                En Producción
              </small>
            </Card.Body>
          </Card>
        </Col>
        
        <Col sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100 border-warning">
            <Card.Body>
              <div className="fs-1 mb-2">🧪</div>
              <h3 className="text-warning fw-bold mb-1">{stats.testingApps}</h3>
              <small className="text-body-secondary text-uppercase fw-semibold">
                En Pruebas
              </small>
            </Card.Body>
          </Card>
        </Col>
        
        <Col sm={6} lg={3} className="mb-3">
          <Card className="text-center h-100 border-info">
            <Card.Body>
              <div className="fs-1 mb-2">✏️</div>
              <h3 className="text-info fw-bold mb-1">{stats.draftApps}</h3>
              <small className="text-body-secondary text-uppercase fw-semibold">
                Borradores
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Actividad reciente */}
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Actividad Reciente</h6>
            </Card.Header>
            <Card.Body>
              {projects.slice(0, 5).map((project, index) => (
                <div key={index} className="d-flex align-items-center p-3 mb-2 bg-body-tertiary rounded">
                  <div className={`rounded-circle me-3 ${
                    project.status === 'deployed' ? 'bg-success' : 
                    project.status === 'testing' ? 'bg-warning' : 'bg-info'
                  }`} style={{ width: '8px', height: '8px' }} />
                  <div className="flex-grow-1">
                    <div className="fw-semibold mb-1">{project.name}</div>
                    <div className="text-body-secondary small">
                      {project.category} • Actualizado {project.lastModified}
                    </div>
                  </div>
                  <Badge bg={
                    project.status === 'deployed' ? 'success' : 
                    project.status === 'testing' ? 'warning' : 'info'
                  }>
                    {project.status}
                  </Badge>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* Distribución por categoría */}
        <Col lg={4}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Categorías de Apps</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">📊 Dashboards</span>
                  <span className="small fw-semibold">{categoryDistribution.dashboard}</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div className="progress-bar bg-primary" style={{ width: `${stats.totalApps > 0 ? (categoryDistribution.dashboard / stats.totalApps) * 100 : 0}%` }}></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">📁 Gestión</span>
                  <span className="small fw-semibold">{categoryDistribution.management}</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div className="progress-bar bg-success" style={{ width: `${stats.totalApps > 0 ? (categoryDistribution.management / stats.totalApps) * 100 : 0}%` }}></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">💬 Comunicación</span>
                  <span className="small fw-semibold">{categoryDistribution.communication}</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div className="progress-bar bg-info" style={{ width: `${stats.totalApps > 0 ? (categoryDistribution.communication / stats.totalApps) * 100 : 0}%` }}></div>
                </div>
              </div>
              
              <div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">🛠️ Utilidades</span>
                  <span className="small fw-semibold">{categoryDistribution.utility}</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div className="progress-bar bg-warning" style={{ width: `${stats.totalApps > 0 ? (categoryDistribution.utility / stats.totalApps) * 100 : 0}%` }}></div>
                </div>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mt-3">
            <Card.Header>
              <h6 className="mb-0">Tips de Uso</h6>
            </Card.Header>
            <Card.Body>
              <div className="text-body-secondary small">
                <p className="mb-2">💡 <strong>Describe detalladamente:</strong> Mientras más específico seas, mejor será el resultado.</p>
                <p className="mb-2">🎨 <strong>Personaliza el diseño:</strong> Elige colores y estilos que representen tu marca.</p>
                <p className="mb-0">📊 <strong>Configura datos:</strong> Define cómo tu app obtendrá y manejará la información.</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// App Generator Tab - FUNCIONAL CON API
const AppGeneratorTab: React.FC<{ 
  onProjectGenerated?: () => void;
  generationStep: number;
  setGenerationStep: (step: number) => void;
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  generationProgress: number;
  setGenerationProgress: (progress: number) => void;
  setActiveTab: (tab: string) => void;
  setSelectedProject: (project: Project | null) => void;
}> = ({ 
  onProjectGenerated, 
  generationStep, 
  setGenerationStep,
  isGenerating,
  setIsGenerating,
  generationProgress,
  setGenerationProgress,
  setActiveTab,
  setSelectedProject
}) => {
  const [appRequest, setAppRequest] = useState<AppGenerationRequest>({
    projectName: '',
    description: '',
    userLevel: 'beginner',
    category: 'custom',
    features: [],
    dataRequirements: { type: 'manual' },
    complexity: 'medium',
    designPreferences: {
      theme: 'light',
      primaryColor: '#007bff',
      layout: 'modern'
    }
  });

  const [generatedProject, setGeneratedProject] = useState<ComponentGeneratorResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deployStatus, setDeployStatus] = useState<string | null>(null);

  // FUNCIÓN RESTAURADA: Generar aplicación con API
const handleGenerate = async () => {
    if (!appRequest.description.trim()) return;
    
    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);
    
    try {
      // Simular proceso de generación en pasos
      const steps = [
        { message: "Analizando descripción...", progress: 20 },
        { message: "Diseñando arquitectura...", progress: 40 },
        { message: "Generando componentes...", progress: 60 },
        { message: "Configurando datos...", progress: 80 },
        { message: "Finalizando aplicación...", progress: 100 }
      ];

      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setGenerationProgress(steps[i].progress);
      }

      // Enriquecer la descripción con información adicional
      let enrichedDescription = appRequest.description.trim();
      
      // Agregar información del tipo de datos a la descripción
      if (appRequest.dataRequirements.type === 'upload') {
        enrichedDescription += ' La aplicación debe permitir subir archivos (CSV, Excel, PDF, etc.)';
      } else if (appRequest.dataRequirements.type === 'api') {
        enrichedDescription += ' La aplicación debe conectarse con APIs externas para obtener datos';
        if (appRequest.dataRequirements.endpoint) {
          enrichedDescription += ` desde ${appRequest.dataRequirements.endpoint}`;
        }
      } else {
        enrichedDescription += ' La aplicación debe permitir entrada manual de datos mediante formularios';
      }

      // Agregar preferencias de diseño a la descripción
      enrichedDescription += ` Usar tema ${appRequest.designPreferences.theme} con diseño ${appRequest.designPreferences.layout}`;

      // Llamar a la API real - CORREGIDO para coincidir con el endpoint
      const response = await fetch('https://backend.anubisai.net/api/component-generator/generate', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: appRequest.projectName,
          description: enrichedDescription,
          projectType: 'react',
          complexity: appRequest.complexity,
          features: appRequest.features,
          baseTemplate: appRequest.category, // usar category como baseTemplate
          deployPath: undefined // opcional
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setGeneratedProject(result);
        if (onProjectGenerated) onProjectGenerated();
      } else {
        console.error('API Error:', response.status, result);
        throw new Error(result.error || `API Error: ${response.status}`);
      }
      
    } catch (error) {
      console.error('Error generating component:', error);
      setError(error instanceof Error ? error.message : 'Error generating component');
      
      // Fallback para desarrollo/testing
      const mockResult: ComponentGeneratorResponse = {
        success: true,
        projectId: `app_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        componentCode: `Generated application code for: ${appRequest.description}`,
        files: [
          { filename: 'App.tsx', content: 'Generated React App...', type: 'component' },
          { filename: 'styles.css', content: 'Generated styles...', type: 'style' }
        ]
      };
      
      setTimeout(() => {
        setGeneratedProject(mockResult);
        if (onProjectGenerated) onProjectGenerated();
      }, 500);
      
    } finally {
      setIsGenerating(false);
    }
  };

  const resetGenerator = () => {
    setGenerationStep(0);
    setGeneratedProject(null);
    setGenerationProgress(0);
    setError(null);
    setAppRequest({
      projectName: '',
      description: '',
      userLevel: 'beginner',
      category: 'custom',
      features: [],
      dataRequirements: { type: 'manual' },
      complexity: 'medium',
      designPreferences: {
        theme: 'light',
        primaryColor: '#007bff',
        layout: 'modern'
      }
    });
  };

  if (isGenerating) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <div className="mb-4">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          </div>
          <h5>🤖 Anubis AI está creando tu aplicación...</h5>
          <p className="text-muted mb-4">Este proceso puede tomar unos momentos</p>
          <ProgressBar 
            now={generationProgress} 
            label={`${generationProgress}%`}
            className="mb-3"
            style={{ height: '20px' }}
          />
          <small className="text-muted">
            {generationProgress === 20 && "Analizando tu descripción y requerimientos..."}
            {generationProgress === 40 && "Diseñando la arquitectura de la aplicación..."}
            {generationProgress === 60 && "Generando componentes React personalizados..."}
            {generationProgress === 80 && "Configurando sistema de datos..."}
            {generationProgress === 100 && "Aplicación generada exitosamente!"}
          </small>
        </Card.Body>
      </Card>
    );
  }

  // CONTINUACIÓN DEL COMPONENTE - PARTE 2

  if (generatedProject) {
    return (
      <div>
        <Alert variant="success" className="mb-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h5 className="alert-heading mb-2">🎉 ¡Aplicación Creada Exitosamente!</h5>
              <p className="mb-0">Tu aplicación está lista para usar.</p>
            </div>
            <Button variant="outline-success" onClick={resetGenerator}>
              Crear Otra App
            </Button>
          </div>
        </Alert>

        <Row>
          <Col lg={8}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Detalles de la Aplicación</h6>
              </Card.Header>
              <Card.Body>
                <h5>Aplicación Generada</h5>
                <p className="text-muted">{appRequest.description}</p>
                
                <div className="mb-3">
                  <strong>Características incluidas:</strong>
                  <div className="mt-2">
                    {appRequest.features.map((feature, index) => (
                      <Badge key={index} bg="primary" className="me-2 mb-1">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="mb-3">
                  <strong>Tipo de datos:</strong> 
                  <Badge bg="info" className="ms-2">
                    {appRequest.dataRequirements.type === 'upload' && '📤 Subida de archivos'}
                    {appRequest.dataRequirements.type === 'api' && '🔗 API externa'}
                    {appRequest.dataRequirements.type === 'manual' && '✏️ Entrada manual'}
                  </Badge>
                </div>

                {error && (
                  <Alert variant="warning" className="mt-3">
                    <strong>⚠️ Conexión con API limitada</strong>
                    <br />
                    <small>{error} - Se usó generación de respaldo</small>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={4}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Próximos Pasos</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button 
  variant="outline-primary"
  onClick={() => {
    const projectName = appRequest.description.split(' ').slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const tempProject: Project = {
      id: generatedProject.projectId,
      name: appRequest.projectName,
      slug: generateSafeSlug(appRequest.projectName),
      type: 'react',
      status: 'generated',
      createdAt: new Date().toISOString().split('T')[0],
      lastModified: new Date().toISOString().split('T')[0],
      description: appRequest.description,
      category: appRequest.category as any,
      complexity: appRequest.complexity,
      features: appRequest.features || [],
      dataSource: { type: appRequest.dataRequirements.type }
    };
    setSelectedProject(tempProject);
    setActiveTab('code-editor');
  }}
>
    <FontAwesomeIcon icon={faEye} className="me-2" />
    Ver Vista Previa
  </Button>
  <Button 
  variant="primary"
  onClick={() => {
    const projectName = appRequest.description.split(' ').slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const tempProject: Project = {
      id: generatedProject.projectId,
      name: projectName,
      slug: generateSafeSlug(projectName), // ← AGREGAR ESTA LÍNEA
      type: 'react',
      status: 'generated',
      createdAt: new Date().toISOString().split('T')[0],
      lastModified: new Date().toISOString().split('T')[0],
      description: appRequest.description,
      category: appRequest.category as any,
      complexity: appRequest.complexity,
      features: appRequest.features || [],
      dataSource: { type: appRequest.dataRequirements.type }
    };
    setSelectedProject(tempProject);
    setActiveTab('preview');
  }}
>
    <FontAwesomeIcon icon={faCode} className="me-2" />
    Editar Código
  </Button>
  <Button 
    variant="success"
    onClick={async () => {
      setDeployStatus('🚀 Iniciando deploy...');
      setTimeout(() => {
        setDeployStatus('✅ Aplicación desplegada exitosamente!');
        if (onProjectGenerated) onProjectGenerated();
      }, 2000);
    }}
  >
    <FontAwesomeIcon icon={faRocket} className="me-2" />
    Desplegar App
  </Button>
</div>
{deployStatus && (
  <Alert 
    variant={deployStatus.includes('✅') ? 'success' : 'info'} 
    className="mt-3"
  >
    <strong>{deployStatus}</strong>
  </Alert>
)}
                
                <hr />
                
                <small className="text-muted">
                  <p>✅ Aplicación generada</p>
                  <p>⏳ Pendiente: Vista previa</p>
                  <p>⏳ Pendiente: Despliegue</p>
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h4>🤖 Generador de Aplicaciones IA</h4>
        <p className="text-muted">
          Describe qué aplicación quieres crear y nosotros la construiremos por ti. 
          No necesitas saber programar - solo explícanos tu idea.
        </p>
      </div>

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Paso 1: Describe tu Aplicación</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
      <Form.Label>
        <strong>Nombre de tu aplicación</strong> <span className="text-danger">*</span>
      </Form.Label>
      <Form.Control
        type="text"
        placeholder="Ej: Gestor de Documentos, Sistema de Ventas, Chat del Equipo..."
        value={appRequest.projectName}
        onChange={(e) => setAppRequest({...appRequest, projectName: e.target.value})}
        maxLength={50}
      />
      <Form.Text className="text-muted">
        Un nombre claro y corto que identifique tu aplicación
      </Form.Text>
    </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>¿Qué aplicación quieres crear?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Ejemplo: Quiero una aplicación para gestionar los documentos de mi empresa. Los usuarios deberían poder subir archivos PDF, organizarlos por categorías, y recibir alertas cuando un documento esté próximo a vencer..."
                  value={appRequest.description}
                  onChange={(e) => setAppRequest({...appRequest, description: e.target.value})}
                />
                <Form.Text className="text-muted">
                  Sé específico: menciona qué debe hacer, quién la usará, y qué características necesitas.
                </Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tu nivel de experiencia</Form.Label>
                    <Form.Select 
                      value={appRequest.userLevel} 
                      onChange={(e) => setAppRequest({...appRequest, userLevel: e.target.value as any})}
                    >
                      <option value="beginner">🔰 Principiante - No sé programar</option>
                      <option value="intermediate">📚 Intermedio - Algo de experiencia</option>
                      <option value="advanced">🚀 Avanzado - Experiencia técnica</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoría de aplicación</Form.Label>
                    <Form.Select 
                      value={appRequest.category} 
                      onChange={(e) => setAppRequest({...appRequest, category: e.target.value})}
                    >
                      <option value="dashboard">📊 Dashboard / Reportes</option>
                      <option value="management">📁 Gestión / Administración</option>
                      <option value="communication">💬 Comunicación / Mensajería</option>
                      <option value="utility">🛠️ Herramienta / Utilidad</option>
                      <option value="custom">🎨 Personalizada</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Complejidad deseada</Form.Label>
                    <Form.Select 
                      value={appRequest.complexity} 
                      onChange={(e) => setAppRequest({...appRequest, complexity: e.target.value as any})}
                    >
                      <option value="simple">🟢 Simple - Funcionalidad básica</option>
                      <option value="medium">🟡 Intermedia - Características avanzadas</option>
                      <option value="complex">🔴 Compleja - Muy personalizada</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>¿Cómo manejará los datos?</Form.Label>
                    <Form.Select 
                      value={appRequest.dataRequirements.type} 
                      onChange={(e) => setAppRequest({
                        ...appRequest, 
                        dataRequirements: { ...appRequest.dataRequirements, type: e.target.value as any }
                      })}
                    >
                      <option value="manual">✏️ Entrada manual - Los usuarios escriben</option>
                      <option value="upload">📤 Subir archivos - CSV, Excel, PDF, etc.</option>
                      <option value="api">🔗 API externa - Conectar con servicios</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Características sugeridas */}
              <Form.Group className="mb-3">
                <Form.Label>Características que podrían incluirse (opcional)</Form.Label>
                <div className="border rounded p-3">
                  {[
                    'Sistema de búsqueda', 'Filtros avanzados', 'Exportar a Excel/PDF', 
                    'Sistema de alertas', 'Gráficos y reportes', 'Subida de archivos',
                    'Sistema de usuarios', 'Notificaciones', 'Modo oscuro/claro'
                  ].map((feature, index) => (
                    <Form.Check
                      key={index}
                      type="checkbox"
                      id={`feature-${index}`}
                      label={feature}
                      checked={appRequest.features.includes(feature)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAppRequest({
                            ...appRequest, 
                            features: [...appRequest.features, feature]
                          });
                        } else {
                          setAppRequest({
                            ...appRequest, 
                            features: appRequest.features.filter(f => f !== feature)
                          });
                        }
                      }}
                      className="mb-2"
                    />
                  ))}
                </div>
                <Form.Text className="text-muted">
                  Estas características se agregarán automáticamente si son relevantes para tu aplicación.
                </Form.Text>
              </Form.Group>

              <Button 
                variant="primary" 
                size="lg"
                className="w-100"
                onClick={handleGenerate}
                disabled={!appRequest.projectName.trim() || !appRequest.description.trim() || appRequest.description.length < 20}
              >
                {isGenerating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generando aplicación...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />
                    🤖 Generar Aplicación con IA
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Guía de Uso</h6>
            </Card.Header>
            <Card.Body>
              <div className="text-body-secondary">
                <h6>Cómo crear tu aplicación:</h6>
                <ol>
                  <li className="mb-2">
                    <strong>Describe:</strong> Explica qué quieres que haga tu aplicación
                  </li>
                  <li className="mb-2">
                    <strong>Configura:</strong> Elige el nivel de complejidad y tipo de datos
                  </li>
                  <li className="mb-2">
                    <strong>Genera:</strong> La IA creará tu aplicación completa
                  </li>
                  <li className="mb-2">
                    <strong>Prueba:</strong> Revisa la vista previa y ajusta si es necesario
                  </li>
                  <li>
                    <strong>Despliega:</strong> Publica tu aplicación para usar
                  </li>
                </ol>
                
                <hr />
                
                <h6>Ejemplos de aplicaciones:</h6>
                <ul className="mb-0">
                  <li>"Sistema para gestionar documentos con alertas de vencimiento"</li>
                  <li>"Dashboard de ventas con gráficos interactivos"</li>
                  <li>"App para enviar mensajes programados al equipo"</li>
                  <li>"Inventario de productos con códigos QR"</li>
                </ul>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mt-3">
            <Card.Header>
              <h6 className="mb-0">Configuración de Datos</h6>
            </Card.Header>
            <Card.Body>
              {appRequest.dataRequirements.type === 'upload' && (
                <div>
                  <Alert variant="info" className="mb-3">
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    <strong>Subida de Archivos</strong><br />
                    Tu app permitirá cargar archivos CSV, Excel, PDF, imágenes, etc.
                  </Alert>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipos de archivo permitidos</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={2}
                      placeholder="CSV, Excel, PDF, Imágenes..."
                      value={appRequest.dataRequirements.structure || ''}
                      onChange={(e) => setAppRequest({
                        ...appRequest,
                        dataRequirements: {
                          ...appRequest.dataRequirements,
                          structure: e.target.value
                        }
                      })}
                    />
                  </Form.Group>
                </div>
              )}
              
              {appRequest.dataRequirements.type === 'api' && (
                <div>
                  <Alert variant="warning" className="mb-3">
                    <FontAwesomeIcon icon={faCloud} className="me-2" />
                    <strong>API Externa</strong><br />
                    Tu app se conectará a servicios externos para obtener datos.
                  </Alert>
                  <Form.Group className="mb-3">
                    <Form.Label>URL del API (opcional)</Form.Label>
                    <Form.Control 
                      type="url"
                      placeholder="https://api.ejemplo.com/datos"
                      value={appRequest.dataRequirements.endpoint || ''}
                      onChange={(e) => setAppRequest({
                        ...appRequest,
                        dataRequirements: {
                          ...appRequest.dataRequirements,
                          endpoint: e.target.value
                        }
                      })}
                    />
                  </Form.Group>
                </div>
              )}
              
              {appRequest.dataRequirements.type === 'manual' && (
                <Alert variant="success">
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  <strong>Entrada Manual</strong><br />
                  Los usuarios podrán introducir datos directamente en formularios.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Projects Tab - FUNCIONAL con manejo de proyectos
const ProjectsTab: React.FC<{ 
  projects: Project[]; 
  onProjectSelect: (project: Project) => void;
  selectedProject: Project | null;
  onDeleteProject: (project: Project) => void;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>; 
  onStatusChange: (projectId: string, status: Project['status']) => void;
}> = ({ projects, onProjectSelect, selectedProject, onDeleteProject, onStatusChange, setProjects }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const filteredProjects = projects.filter(project => {
    const statusMatch = filterStatus === 'all' || project.status === filterStatus;
    const categoryMatch = filterCategory === 'all' || project.category === filterCategory;
    return statusMatch && categoryMatch;
  });

  const handleEditProject = (project: Project) => {
    setEditingProject({ ...project });
    setShowEditModal(true);
  };

  const handleSaveProject = async () => {
    if (!editingProject) return;
    
    try {
      const updatedProject = {
      ...editingProject,
      slug: generateSafeSlug(editingProject.name)
    };
      // Llamar a API de actualización
      const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${editingProject.id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: updatedProject.name,
        slug: updatedProject.slug,
        description: updatedProject.description,
        status: updatedProject.status
      })
    });

if (response.ok) {
      console.log('Project updated successfully');
      // Actualizar proyecto local
      setProjects(prev => prev.map(p => 
        p.id === editingProject.id ? updatedProject : p
      ));
    } else {
      console.error('Update API Error:', response.status);
    }
      
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating project:', error);
      setShowEditModal(false);
    }
  };

  const handleCloneProject = async (project: Project) => {
    try {
      const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${project.id}/clone`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
    name: `${project.name} (Copia)`
  })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Project cloned:', result);
      } else {
        console.error('Clone API Error:', response.status);
      }
    } catch (error) {
      console.error('Error cloning project:', error);
    }
  };

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'deployed': return 'success';
      case 'testing': return 'warning';
      case 'generated': return 'info';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: Project['status']) => {
    switch (status) {
      case 'deployed': return faCheckCircle;
      case 'testing': return faPause;
      case 'generated': return faPlay;
      default: return faTimesCircle;
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Mis Aplicaciones</h4>
        <div className="d-flex gap-2">
          <Form.Select 
            size="sm" 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="all">Todos los estados</option>
            <option value="draft">Borradores</option>
            <option value="generated">Generadas</option>
            <option value="testing">En pruebas</option>
            <option value="deployed">En producción</option>
          </Form.Select>
          <Form.Select 
            size="sm" 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="all">Todas las categorías</option>
            <option value="dashboard">Dashboards</option>
            <option value="management">Gestión</option>
            <option value="communication">Comunicación</option>
            <option value="utility">Utilidades</option>
          </Form.Select>
        </div>
      </div>
      
      {filteredProjects.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <div className="fs-1 mb-3">📱</div>
            <h5>No hay aplicaciones</h5>
            <p className="text-body-secondary">
              {projects.length === 0 
                ? "Aún no has creado ninguna aplicación. ¡Comienza generando tu primera app!"
                : "No hay aplicaciones que coincidan con los filtros seleccionados."
              }
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {filteredProjects.map(project => (
            <Col key={project.id} md={6} lg={4} className="mb-4">
              <Card 
                className={`h-100 project-card ${selectedProject?.id === project.id ? 'border-primary' : ''}`}
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              >
                <Card.Header className="d-flex align-items-center justify-content-between pb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon 
                      icon={getStatusIcon(project.status)} 
                      className={`text-${getStatusColor(project.status)} me-2`} 
                    />
                    <Badge bg={getStatusColor(project.status)} className="text-uppercase small">
                      {project.status}
                    </Badge>
                  </div>
                  <div className="d-flex gap-1">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProject(project);
                      }}
                      title="Editar"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloneProject(project);
                      }}
                      title="Clonar"
                    >
                      <FontAwesomeIcon icon={faClone} />
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteProject(project);
                      }}
                      title="Eliminar"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </Card.Header>
                
                <Card.Body onClick={() => onProjectSelect(project)}>
                  <h6 className="mb-2">{project.name}</h6>
                  <p className="text-body-secondary small mb-3" style={{ minHeight: '40px' }}>
                    {project.description}
                  </p>
                  
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-1">
                      {(project.features || []).slice(0, 2).map((feature: string, index: number) => (
                        <Badge key={index} bg="light" text="dark" className="small">
                          {feature}
                        </Badge>
                      ))}
                      {(project.features || []).length > 2 && (
                        <Badge bg="light" text="dark" className="small">
                          +{project.features.length - 2} más
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-center justify-content-between">
                    <small className="text-body-tertiary">
                      {project.lastModified}
                    </small>
                    <div className="d-flex gap-1">
                      {project.dataSource?.type === 'upload' && 
                        <FontAwesomeIcon icon={faUpload} className="text-muted" title="Subida de archivos" />}
                      {project.dataSource?.type === 'api' && 
                        <FontAwesomeIcon icon={faLink} className="text-muted" title="API externa" />}
                      {project.dataSource?.type === 'manual' && 
                        <FontAwesomeIcon icon={faEdit} className="text-muted" title="Entrada manual" />}
                    </div>
                  </div>
                </Card.Body>
                
                <Card.Footer className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="flex-fill"
                    onClick={(e) => {
                      e.stopPropagation();
                      onProjectSelect(project);
                    }}
                  >
                    <FontAwesomeIcon icon={faEye} className="me-1" />
                    Ver
                  </Button>
                  
                  {project.status === 'generated' && (
                    <Button 
                      variant="warning" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange(project.id, 'testing');
                      }}
                    >
                      <FontAwesomeIcon icon={faPause} className="me-1" />
                      Probar
                    </Button>
                  )}
                  
                  {project.status === 'testing' && (
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange(project.id, 'deployed');
                      }}
                    >
                      <FontAwesomeIcon icon={faRocket} className="me-1" />
                      Deploy
                    </Button>
                  )}
                  
                  {project.status === 'deployed' && project.deployUrl && (
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(project.deployUrl, '_blank');
                      }}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      Abrir
                    </Button>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Modal de edición */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Aplicación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProject && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Nombre de la aplicación</Form.Label>
                <Form.Control
                  type="text"
                  value={editingProject.name}
                  onChange={(e) => setEditingProject({
                    ...editingProject,
                    name: e.target.value
                  })}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({
                    ...editingProject,
                    description: e.target.value
                  })}
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      value={editingProject.status}
                      onChange={(e) => setEditingProject({
                        ...editingProject,
                        status: e.target.value as Project['status']
                      })}
                    >
                      <option value="draft">Borrador</option>
                      <option value="generated">Generada</option>
                      <option value="testing">En pruebas</option>
                      <option value="deployed">En producción</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoría</Form.Label>
                    <Form.Select
                      value={editingProject.category}
                      onChange={(e) => setEditingProject({
                        ...editingProject,
                        category: e.target.value as Project['category']
                      })}
                    >
                      <option value="dashboard">Dashboard</option>
                      <option value="management">Gestión</option>
                      <option value="communication">Comunicación</option>
                      <option value="utility">Utilidad</option>
                      <option value="custom">Personalizada</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveProject}>
            <FontAwesomeIcon icon={faCogs} className="me-2" />
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EnhancedComponentStudio;




/* @v0.1 init - funcional 
import React, { useState, useEffect } from 'react';
import { Nav, Tab, Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMagicWandSparkles, 
  faEye, 
  faCode, 
  faChartBar, 
  faFolderTree,
  faPlus,
  faRefresh,
  faRocket,
  faCopy,
  faSave,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { getHatateToken } from '../helpers/utils';

// Interfaces
interface Project {
  id: string;
  name: string;
  type: 'react' | 'vue' | 'angular';
  status: 'draft' | 'generated' | 'deployed';
  createdAt: string;
  lastModified: string;
  description: string;
  projectPath?: string;
}

interface ComponentGeneratorResponse {
  success: boolean;
  projectId: string;
  componentCode: string;
  files: Array<{
    filename: string;
    content: string;
    type: 'component' | 'style' | 'config' | 'test';
  }>;
  projectPath?: string;
  previewUrl?: string;
}

   const getAuthHeaders = () => {
  const token = getHatateToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': token  // ✅ SIN "Bearer " - igual que tu ejemplo funcional
  };
};

const ComponentStudio: React.FC = () => {
  const [activeTab, setActiveTab] = useState('generator');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Cargar proyectos al inicio
  useEffect(() => {
    loadProjects();
  }, []);

const loadProjects = async () => {
  try {
    const response = await fetch('https://backend.anubisai.net/api/component-generator/projects', {
      method: 'GET',
      headers: getAuthHeaders()  // ✅ Usar helper
    });

    if (response.ok) {
      const result = await response.json();
      setProjects(result.projects || []);
    } else {
      console.error('API Error:', response.status, response.statusText);
      // Fallback a proyectos mock
      setProjects([
        {
          id: 'comp_1719677123456_abc123',
          name: 'Dashboard Analytics',
          type: 'react',
          status: 'deployed',
          createdAt: '2025-06-28',
          lastModified: '2025-06-29',
          description: 'Dashboard con métricas y gráficos interactivos'
        },
        {
          id: 'comp_1719677234567_def456',
          name: 'User Profile Card',
          type: 'react',
          status: 'generated',
          createdAt: '2025-06-29',
          lastModified: '2025-06-29',
          description: 'Tarjeta de perfil con avatar y estadísticas'
        }
      ]);
    }
  } catch (error) {
    console.error('Error loading projects:', error);
    // Fallback en caso de error
    setProjects([
      {
        id: 'comp_1719677123456_abc123',
        name: 'Dashboard Analytics',
        type: 'react',
        status: 'deployed',
        createdAt: '2025-06-28',
        lastModified: '2025-06-29',
        description: 'Dashboard con métricas y gráficos interactivos'
      }
    ]);
  }
};

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setActiveTab('code-editor');
  };

  const handleProjectGenerated = () => {
    loadProjects();
  };

  return (
    <Container fluid className="px-0">
      <Row className="g-0">
        <Col>
          {/* Header * /}
          <div className="bg-body-tertiary border-bottom p-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h1 className="h3 mb-1 text-body-emphasis">
                  🎨 Anubis AI Component Studio
                </h1>
                <p className="text-body-secondary mb-0">
                  Create, preview, and deploy React components with Anubis AI assistance
                </p>
              </div>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={loadProjects}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => setActiveTab('generator')}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  New Component
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs * /}
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'generator')}>
            <div className="bg-body border-bottom">
              <Nav variant="tabs" className="border-0 px-4">
                <Nav.Item>
                  <Nav.Link eventKey="generator" className="border-0 py-3">
                    <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />
                    Generator
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="projects" className="border-0 py-3">
                    <FontAwesomeIcon icon={faFolderTree} className="me-2" />
                    Projects ({projects.length})
                  </Nav.Link>
                </Nav.Item>
                {selectedProject && (
                  <>
                    <Nav.Item>
                      <Nav.Link eventKey="code-editor" className="border-0 py-3">
                        <FontAwesomeIcon icon={faCode} className="me-2" />
                        {selectedProject.name}
                        <Badge bg="secondary" className="ms-2">{selectedProject.type}</Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="preview" className="border-0 py-3">
                        <FontAwesomeIcon icon={faEye} className="me-2" />
                        Preview
                      </Nav.Link>
                    </Nav.Item>
                  </>
                )}
                <Nav.Item>
                  <Nav.Link eventKey="analytics" className="border-0 py-3">
                    <FontAwesomeIcon icon={faChartBar} className="me-2" />
                    Analytics
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            {/* Tab Content * /}
            <div className="p-4">
              <Tab.Content>
                <Tab.Pane eventKey="generator">
                  <GeneratorTab onProjectGenerated={handleProjectGenerated} />
                </Tab.Pane>
                
                <Tab.Pane eventKey="projects">
                  <ProjectsTab 
                    projects={projects} 
                    onProjectSelect={handleProjectSelect}
                    selectedProject={selectedProject}
                  />
                </Tab.Pane>
                
                <Tab.Pane eventKey="code-editor">
                  <CodeEditorTab project={selectedProject} />
                </Tab.Pane>
                
                <Tab.Pane eventKey="preview">
                  <PreviewTab project={selectedProject} />
                </Tab.Pane>
                
                <Tab.Pane eventKey="analytics">
                  <AnalyticsTab />
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        </Col>
      </Row>
    </Container>
  );
};

// Generator Tab Component
const GeneratorTab: React.FC<{ onProjectGenerated?: () => void }> = ({ onProjectGenerated }) => {
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState('react');
  const [complexity, setComplexity] = useState('medium');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedProject, setGeneratedProject] = useState<ComponentGeneratorResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

const handleGenerate = async () => {
  if (!description.trim()) return;
  
  setIsGenerating(true);
  setError(null);
  
  try {
    const response = await fetch('https://backend.anubisai.net/api/component-generator/generate', {
      method: 'POST',
      headers: getAuthHeaders(),  // ✅ Usar helper
      body: JSON.stringify({
        description: description.trim(),
        projectType,
        complexity,
        features: []
      })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      setGeneratedProject(result);
      if (onProjectGenerated) onProjectGenerated();
    } else {
      console.error('API Error:', response.status, result);
      throw new Error(result.error || `API Error: ${response.status}`);
    }
    
  } catch (error) {
    console.error('Error generating component:', error);
    setError(error instanceof Error ? error.message : 'Error generating component');
    
    // Fallback para desarrollo/testing
    const mockResult: ComponentGeneratorResponse = {
      success: true,
      projectId: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      componentCode: `import React from 'react';\nimport { Card, Button } from 'react-bootstrap';\n\nconst GeneratedComponent = () => {\n  return (\n    <Card>\n      <Card.Body>\n        <h5 className="card-title">Generated Component</h5>\n        <p className="card-text">${description}</p>\n        <Button variant="primary">Action</Button>\n      </Card.Body>\n    </Card>\n  );\n};\n\nexport default GeneratedComponent;`,
      files: [{ filename: 'GeneratedComponent.tsx', content: 'component code', type: 'component' }]
    };
    
    setTimeout(() => {
      setGeneratedProject(mockResult);
      if (onProjectGenerated) onProjectGenerated();
    }, 1000);
    
  } finally {
    setIsGenerating(false);
  }
};

  return (
    <Row>
      <Col lg={6}>
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />
              Anubis AI Component Generator
            </h5>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Describe your component</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="e.g., A dashboard with charts and metrics for user analytics..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>
              
              <Row className="mb-3">
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Framework</Form.Label>
                    <Form.Select 
                      value={projectType} 
                      onChange={(e) => setProjectType(e.target.value)}
                      disabled={isGenerating}
                    >
                      <option value="react">React</option>
                      <option value="vue">Vue</option>
                      <option value="angular">Angular</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Complexity</Form.Label>
                    <Form.Select 
                      value={complexity} 
                      onChange={(e) => setComplexity(e.target.value)}
                      disabled={isGenerating}
                    >
                      <option value="simple">Simple</option>
                      <option value="medium">Medium</option>
                      <option value="complex">Complex</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="primary" 
                size="lg"
                className="w-100"
                onClick={handleGenerate}
                disabled={!description.trim() || isGenerating}
              >
                {isGenerating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generating component...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" />
                    Generate Component
                  </>
                )}
              </Button>
            </Form>

            {error && (
              <Alert variant="warning" className="mt-3">
                <strong>⚠️ API Connection Issue</strong>
                <br />
                <small>{error} - Using fallback generation</small>
              </Alert>
            )}

            {generatedProject && (
              <Alert variant="success" className="mt-3">
                <strong>✅ Component Generated Successfully!</strong>
                <br />
                <small>Project ID: {generatedProject.projectId}</small>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Col>
      
      <Col lg={6}>
        <Card>
          <Card.Header>
            <h5 className="mb-0">Quick Start Guide</h5>
          </Card.Header>
          <Card.Body>
            <div className="text-body-secondary">
              <h6>How to use Component Studio:</h6>
              <ol>
                <li className="mb-2">
                  <strong>Describe:</strong> Write a clear description of the component you want to create
                </li>
                <li className="mb-2">
                  <strong>Configuer:</strong> Choose framework and complexity level
                </li>
                <li className="mb-2">
                  <strong>Generate:</strong> Click generate and wait for AI to create your component
                </li>
                <li className="mb-2">
                  <strong>Edit:</strong> Use the code editor to modify the generated code
                </li>
                <li>
                  <strong>Deploy:</strong> Preview and deploy your component
                </li>
              </ol>
              
              <hr />
              
              <h6>Example descriptions:</h6>
              <ul className="mb-0">
                <li>"A user profile card with avatar, name, and social stats"</li>
                <li>"A responsive dashboard with charts and metrics"</li>
                <li>"A contact form with validation and submit button"</li>
                <li>"A navigation menu with dropdown options"</li>
              </ul>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

// Projects Tab Component
const ProjectsTab: React.FC<{ 
  projects: Project[]; 
  onProjectSelect: (project: Project) => void;
  selectedProject: Project | null;
}> = ({ projects, onProjectSelect, selectedProject }) => {
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Your Projects</h4>
        <Badge bg="primary">{projects.length} projects</Badge>
      </div>
      
      {projects.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <FontAwesomeIcon icon={faFolderTree} size="3x" className="text-muted mb-3" />
            <h5>No projects yet</h5>
            <p className="text-body-secondary">
              Create your first component using the Generator tab
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {projects.map(project => (
            <Col key={project.id} md={6} lg={4} className="mb-4">
              <Card 
                className={`h-100 ${selectedProject?.id === project.id ? 'border-primary' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => onProjectSelect(project)}
              >
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">{project.name}</h6>
                    <Badge 
                      bg={project.status === 'deployed' ? 'success' : 
                          project.status === 'generated' ? 'warning' : 'secondary'}
                    >
                      {project.status}
                    </Badge>
                  </div>
                  <p className="text-body-secondary small mb-3">
                    {project.description}
                  </p>
                  <div className="d-flex align-items-center justify-content-between">
                    <Badge bg="light">{project.type}</Badge>
                    <small className="text-body-tertiary">
                      {project.lastModified}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

// Code Editor Tab Component
const CodeEditorTab: React.FC<{ project?: Project | null }> = ({ project }) => {
  const [activeFile, setActiveFile] = useState('component.tsx');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      loadProjectCode();
    }
  }, [project]);

const loadProjectCode = async () => {
  if (!project?.id) return;
  
  setIsLoading(true);
  try {
    const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${project.id}/files`, {
      method: 'GET',
      headers: getAuthHeaders()  // ✅ Usar helper
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success && result.files && result.files.length > 0) {
        // Buscar el archivo principal del componente
        const mainFile = result.files.find(f => f.type === 'component') || result.files[0];
        setCode(mainFile.content);
      } else {
        throw new Error('No files found in project');
      }
    } else {
      console.error('Load code API Error:', response.status);
      throw new Error(`API Error: ${response.status}`);
    }
  } catch (error) {
    console.error('Error loading project code:', error);
    
    // Código mock como fallback
    const mockCode = `import React from 'react';
import { Card, Button } from 'react-bootstrap';

const ${project.name?.replace(/\s/g, '') || 'GeneratedComponent'} = () => {
  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">${project.name || 'Generated Component'}</h5>
      </Card.Header>
      <Card.Body>
        <p className="text-body-secondary">
          ${project.description || 'This is a generated component.'}
        </p>
        <Button variant="primary">Action Button</Button>
      </Card.Body>
    </Card>
  );
};

export default ${project.name?.replace(/\s/g, '') || 'GeneratedComponent'};`;
    setCode(mockCode);
  } finally {
    setIsLoading(false);
  }
};

const handleSave = async () => {
  if (!project?.id || !code.trim()) return;
  
  setIsSaving(true);
  setSaveStatus(null);
  
  try {
    const response = await fetch(`https://backend.anubisai.net/api/component-generator/projects/${project.id}/files`, {
      method: 'PUT',
      headers: getAuthHeaders(),  // ✅ Usar helper
      body: JSON.stringify({
        filename: activeFile,
        content: code
      })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      setSaveStatus('✅ File saved successfully!');
    } else {
      console.error('Save API Error:', response.status, result);
      throw new Error(result.error || 'Save failed');
    }
  } catch (error) {
    console.error('Save error:', error);
    setSaveStatus(`❌ Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    
    // Fallback local
    console.log('Saving code locally for project:', project.id);
    setSaveStatus('✅ File saved (local cache)');
  } finally {
    setIsSaving(false);
  }
};

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setSaveStatus('📋 Code copied to clipboard!');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      setSaveStatus('❌ Failed to copy code');
    }
  };

  const handleExport = async () => {
    try {
      const blob = new Blob([code], { type: 'text/typescript' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = activeFile;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSaveStatus('📤 File exported successfully!');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      setSaveStatus('❌ Export failed');
    }
  };

  if (!project) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <FontAwesomeIcon icon={faCode} size="3x" className="text-muted mb-3" />
          <h5>No project selected</h5>
          <p className="text-body-secondary">
            Select a project from the Projects tab to start editing
          </p>
        </Card.Body>
      </Card>
    );
  }

  const files = ['component.tsx', 'styles.css', 'index.ts', 'package.json'];

  return (
    <div>
      {/* File Tabs * /}
      <Card className="mb-3">
        <Card.Header className="pb-0">
          <Nav variant="tabs" className="border-0">
            {files.map(file => (
              <Nav.Item key={file}>
                <Nav.Link 
                  active={activeFile === file}
                  onClick={() => setActiveFile(file)}
                  className="border-0"
                >
                  📄 {file}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Card.Header>
      </Card>

      {/* Toolbar * /}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <h6 className="mb-0">{project.name}</h6>
              <Badge bg="primary">{project.type}</Badge>
              {isLoading && <Spinner size="sm" />}
            </div>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={handleCopy}
                disabled={!code.trim()}
              >
                <FontAwesomeIcon icon={faCopy} className="me-1" />
                Copy
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleSave}
                disabled={isSaving || !code.trim()}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-1" />
                    Save
                  </>
                )}
              </Button>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleExport}
                disabled={!code.trim()}
              >
                <FontAwesomeIcon icon={faDownload} className="me-1" />
                Export
              </Button>
            </div>
          </div>
          
          {saveStatus && (
            <Alert 
              variant={saveStatus.includes('✅') || saveStatus.includes('📋') || saveStatus.includes('📤') ? 'success' : 'danger'} 
              className="mt-2 mb-0"
            >
              <small>{saveStatus}</small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Code Editor * /}
      <Card>
        <Card.Body className="p-0">
          <Form.Control
            as="textarea"
            className="border-0"
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              lineHeight: '1.5',
              height: '500px',
              resize: 'vertical' as const
            }}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="// Your component code will appear here..."
            disabled={isLoading}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

// Preview Tab Component
const PreviewTab: React.FC<{ project?: Project | null }> = ({ project }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployStatus, setDeployStatus] = useState<string | null>(null);

  useEffect(() => {
    if (project?.id) {
      setIsLoading(true);
      setTimeout(() => {
        setPreviewUrl(`https://backend.anubisai.net/api/component-generator/preview/${project.id}`);
        setIsLoading(false);
      }, 1000);
    }
  }, [project]);

const handleDeploy = async () => {
  if (!project?.id) return;
  
  setIsDeploying(true);
  setDeployStatus(null);
  
  try {
    const response = await fetch('https://backend.anubisai.net/api/component-generator/deploy', {
      method: 'POST',
      headers: getAuthHeaders(),  // ✅ Usar helper
      body: JSON.stringify({
        projectId: project.id,
        deployPath: `/tmp/deployed/${project.id}`,
        runBuild: true
      })
    });

    const result = await response.json();

    if (response.ok && result.success) {
      setDeployStatus('✅ Deploy completed successfully!');
    } else {
      console.error('Deploy API Error:', response.status, result);
      throw new Error(result.error || 'Deploy failed');
    }
    
  } catch (error) {
    console.error('Deploy error:', error);
    setDeployStatus(`❌ Deploy failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    
    // Fallback para desarrollo
    setTimeout(() => {
      setDeployStatus('✅ Deploy completed (fallback mode)');
    }, 2000);
  } finally {
    setIsDeploying(false);
  }
};

  const handleRefresh = () => {
    if (project?.id) {
      setIsLoading(true);
      setTimeout(() => {
        setPreviewUrl(`${previewUrl}?t=${Date.now()}`);
        setIsLoading(false);
      }, 500);
    }
  };

  if (!project) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <FontAwesomeIcon icon={faEye} size="3x" className="text-muted mb-3" />
          <h5>No project selected</h5>
          <p className="text-body-secondary">
            Select a project to see the live preview
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      {/* Preview Controls * /}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="mb-0">
              <FontAwesomeIcon icon={faEye} className="me-2" />
              Live Preview - {project.name}
            </h6>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading || !project}
              >
                <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
              </Button>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleDeploy}
                disabled={isDeploying || !project}
              >
                {isDeploying ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRocket} className="me-1" />
                    Deploy
                  </>
                )}
              </Button>
            </div>
          </div>

          {deployStatus && (
            <Alert 
              variant={deployStatus.includes('✅') ? 'success' : 'danger'} 
              className="mt-2 mb-0"
              dismissible 
              onClose={() => setDeployStatus(null)}
            >
              <small>{deployStatus}</small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Preview Area * /}
      <Card>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
              <div className="text-center">
                <Spinner className="mb-3" />
                <p className="text-body-secondary mb-0">Loading preview...</p>
              </div>
            </div>
          ) : (
            <div style={{ height: '500px' }}>
              <iframe
                src={previewUrl || 'about:blank'}
                className="w-100 h-100 border-0"
                title="Component Preview"
                style={{ borderRadius: '0.375rem' }}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab: React.FC = () => {
  const [stats, setStats] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

const loadAnalytics = async () => {
  setIsLoading(true);
  try {
    const response = await fetch('https://backend.anubisai.net/api/component-generator/analytics', {
      method: 'GET',
      headers: getAuthHeaders()  // ✅ Usar helper
    });

    if (response.ok) {
      const result = await response.json();
      setStats(result.stats || [
        { label: 'Total Components', value: '12', color: 'primary', icon: '🧩' },
        { label: 'Deployed', value: '8', color: 'success', icon: '🚀' },
        { label: 'Avg Build Time', value: '2.3s', color: 'warning', icon: '⚡' },
        { label: 'Success Rate', value: '95%', color: 'info', icon: '✅' }
      ]);
      setRecentActivity(result.recentActivity || [
        { action: 'Component Generated', component: 'UserProfile', time: '2 min ago', status: 'success' },
        { action: 'Deploy Completed', component: 'Dashboard', time: '15 min ago', status: 'success' },
        { action: 'Build Failed', component: 'DataTable', time: '1 hour ago', status: 'error' },
        { action: 'Component Generated', component: 'NavBar', time: '3 hours ago', status: 'success' }
      ]);
    } else {
      console.error('Analytics API Error:', response.status);
      throw new Error(`API Error: ${response.status}`);
    }
  } catch (error) {
    console.error('Error loading analytics:', error);
    // Usar datos mock como fallback
    setStats([
      { label: 'Total Components', value: '12', color: 'primary', icon: '🧩' },
      { label: 'Deployed', value: '8', color: 'success', icon: '🚀' },
      { label: 'Avg Build Time', value: '2.3s', color: 'warning', icon: '⚡' },
      { label: 'Success Rate', value: '95%', color: 'info', icon: '✅' }
    ]);
    setRecentActivity([
      { action: 'Component Generated', component: 'UserProfile', time: '2 min ago', status: 'success' },
      { action: 'Deploy Completed', component: 'Dashboard', time: '15 min ago', status: 'success' },
      { action: 'Build Failed', component: 'DataTable', time: '1 hour ago', status: 'error' },
      { action: 'Component Generated', component: 'NavBar', time: '3 hours ago', status: 'success' }
    ]);
  } finally {
    setIsLoading(false);
  }
};

const debugToken = () => {
  const token = getHatateToken();
  console.log('🔍 Token Debug:');
  console.log('- Token exists:', !!token);
  console.log('- Token type:', typeof token);
  console.log('- Token length:', token?.length || 0);
  console.log('- Token preview:', token?.substring(0, 20) + '...' || 'None');
  
  // Verificar si el token tiene el formato correcto
  if (token) {
    try {
      // Si tu token es JWT, decodificar para verificar
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        console.log('- JWT payload preview:', {
          userId: payload.userId || payload.sub,
          exp: payload.exp ? new Date(payload.exp * 1000) : 'No expiry',
          iat: payload.iat ? new Date(payload.iat * 1000) : 'No issued time'
        });
      }
    } catch (e) {
      console.log('- Token is not JWT format or is corrupted');
    }
  }
};

// 🔧 EXTRA: Test de conexión
const testConnection = async () => {
  try {
    console.log('🔍 Testing API connection...');
    debugToken();
    
    const response = await fetch('https://backend.anubisai.net/api/component-generator/projects', {
      method: 'GET',
      headers: getAuthHeaders()
    });
    
    console.log('📊 API Response:');
    console.log('- Status:', response.status);
    console.log('- Headers:', Object.fromEntries(response.headers.entries()));
    
    const text = await response.text();
    console.log('- Response preview:', text.substring(0, 200));
    
    if (response.status === 401) {
      console.error('❌ Authentication failed - token may be invalid or expired');
    } else if (response.status === 403) {
      console.error('❌ Authorization failed - user may not have permissions');
    } else if (response.ok) {
      console.log('✅ API connection successful');
    } else {
      console.error(`❌ API error: ${response.status} ${response.statusText}`);
    }
    
  } catch (error) {
    console.error('❌ Connection failed:', error);
  }
};

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner className="mb-3" />
          <p className="text-body-secondary mb-0">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">
          <FontAwesomeIcon icon={faChartBar} className="me-2" />
          Project Analytics
        </h4>
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={loadAnalytics}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
        </Button>
      </div>
      
      {/* Stats Grid * /}
      <Row className="mb-4">
        {stats.map((stat, index) => (
          <Col key={index} sm={6} lg={3} className="mb-3">
            <Card className="text-center h-100">
              <Card.Body>
                <div className="fs-1 mb-2">{stat.icon}</div>
                <h3 className={`text-${stat.color} fw-bold mb-1`}>
                  {stat.value}
                </h3>
                <small className="text-body-secondary text-uppercase fw-semibold">
                  {stat.label}
                </small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Recent Activity * /}
      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Recent Activity</h6>
            </Card.Header>
            <Card.Body>
              <div className="activity-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {recentActivity.map((activity, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center p-3 mb-2 bg-body-tertiary rounded"
                  >
                    <div 
                      className={`rounded-circle me-3 ${
                        activity.status === 'success' ? 'bg-success' : 'bg-danger'
                      }`}
                      style={{ width: '8px', height: '8px' }}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold mb-1">
                        {activity.action}
                      </div>
                      <div className="text-body-secondary small">
                        {activity.component} • {activity.time}
                      </div>
                    </div>
                    <Badge 
                      bg={activity.status === 'success' ? 'success' : 'danger'}
                      className="ms-2"
                    >
                      {activity.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Quick Stats</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">Deployment Success</span>
                  <span className="small fw-semibold">95%</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-success" 
                    style={{ width: '95%' }}
                  ></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">Code Quality</span>
                  <span className="small fw-semibold">88%</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-info" 
                    style={{ width: '88%' }}
                  ></div>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small">AI Accuracy</span>
                  <span className="small fw-semibold">92%</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-warning" 
                    style={{ width: '92%' }}
                  ></div>
                </div>
              </div>
              
              <hr />
              
              <div className="text-center">
                <h5 className="text-primary mb-1">156</h5>
                <small className="text-body-secondary">Total Lines Generated</small>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mt-3">
            <Card.Header>
              <h6 className="mb-0">Popular Frameworks</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small">React</span>
                <Badge bg="primary">75%</Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small">Vue</span>
                <Badge bg="success">20%</Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="small">Angular</span>
                <Badge bg="info">5%</Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ComponentStudio; */